import { pinScheduleCard } from "../../lib/pinScheduleCard";
import { legacyPath } from "../../lib/prettyUrls";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useTimePrefs } from "../../lib/timePrefs";
import { PWHL_CONFERENCES } from "../../lib/pwhlConferences";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, assetUrl, getSettings, getPlayers, getBusinessRevenue, saveBusinessRevenue, getPredictionsGrid, saveGamePrediction, deleteGamePrediction, getGamePredictions } from "../../lib/api";
import { eventCardStyle, EventTitle, homeOpenerEvent } from "../../lib/nhlSpecialEvents";
import { toast } from "sonner";
import { SegmentedControl } from "../SegmentedControl";
import { Loader } from "../Loader";
import { useTeamLookup } from "../../lib/teamLookup";
import { SideLineupOverlay } from "../../pages/NhlSchedule";
import { PwhlSideLineup } from "./PwhlSideLineup";
import { recStr } from "../../lib/pwhlPredict";
import { TEAM_INFO, orderedTeamIdsForSeason } from "../../lib/teamMap";
import { TeamBadge } from "../TeamBadge";
import { PlayerAvatar } from "../PlayerAvatar";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, CalendarDays, CalendarClock, Sparkles, DollarSign, ClipboardList, Users, AlertTriangle, X } from "lucide-react";
import { SeasonSwitcher, useSeasonList } from "../SeasonSwitcher";
import { shortSeason } from "../../lib/format";
import { formatFixtureTime } from "../../lib/timeFormat";
import { useMobileSubtabs } from "../../lib/MobileNavContext";
import { DayStrip } from "./DayStrip";
import { useGameNav } from "../../context/GameNavContext";
import { NavPageTitle } from "../NavPageTitle";
import MobilePageControls from "../MobilePageControls";
import { resolveSize, useDeviceKind } from "../../lib/standingsRender";
import { useSortByTeam } from "../../lib/SortByTeamContext";
import { useAccountFavs, favRankOf } from "../../lib/useAccountFavs";
import { useGameDelay, useDelayedSnapshot } from "../../lib/useGameDelay";
import { cardSizeOf } from "./CardSize";
import { buildStories } from "../../lib/storiesData";
import { MiniStory } from "../stories/StoryBits";
import { useBusinessMode } from "../../lib/BusinessModeContext";
import { useAuth } from "../../lib/AuthContext";
import { useAdminMode } from "../../lib/AdminModeContext";
import { ScheduleBusinessCtx, useScheduleBusiness, gameRevenue, hasGameOverride, money as bizMoney } from "../../lib/businessRevenue";
import GameCentre from "../../pages/GameCentre";
import { usePagePrefs } from "../../lib/PagePrefs";
import GamePredict from "./GamePredict";
import { gameHref, syncGameUrl, auxHandlers } from "../../lib/seo";
import { pastLast, pwhlUtc } from "../../lib/scheduleOrder";
import { GameCard as NhlGameCard } from "../../pages/NhlSchedule";
import NhlGameCentrePanel, { NhlSideLineup } from "../NhlGameCentrePanel";
import { NhlApiBase } from "../../lib/nhlApiBase";
import { ScheduleTimelineBar } from "./TimelineBar";
import { sideGutterVp, colsForWidth, sideBoardsFit } from "../../lib/sideGutter";
import { topChromeBottom } from "../../lib/stickyTop";

export default function PWHLSchedule({ teamId = null }) {
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
    const { setTeamIds, pwhlSurfMulti, pwhlSurfGroup } = useSortByTeam();
    const { businessMode } = useBusinessMode();
    const { isAdmin, user } = useAuth();
    const { getPref, setPref } = usePagePrefs();
    const seasonList = useSeasonList();
    const qc = useQueryClient();
    const { data: biz } = useQuery({ queryKey: ["business-revenue"], queryFn: getBusinessRevenue, enabled: businessMode });
    const saveBiz = useMutation({
        mutationFn: (body) => saveBusinessRevenue(body),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["business-revenue"] }),
    });
    const setGameRevenue = (gameId, amount) => saveBiz.mutate({ per_game: { [gameId]: amount === "" || amount == null ? null : Number(amount) } });
    const setStageDefault = (season, stage, amount) => saveBiz.mutate({ stage_defaults: { [`${season}|${stage}`]: amount === "" || amount == null ? null : Number(amount) } });

    // Fetch the ENTIRE schedule (all seasons + all stages) ONCE so the week
    // arrows form a single continuous canonical timeline from the first week of
    // preseason (2024) to the last available week. Season/stage are DERIVED from
    // the currently-shown week, not used to filter the fetch.
    const { data: rawAll, isLoading } = useQuery({
        queryKey: ["pwhl-schedule-all", teamId || "league"],
        queryFn: () => api.get(`/pwhl/schedule${teamId ? `?team_id=${teamId}` : ""}`).then((r) => r.data),
    });
    const delaySec = useGameDelay();
    const anyLive = (rawAll || []).some((f) => /live|progress|period/i.test(f.status || ""));
    const allData = useDelayedSnapshot(rawAll, delaySec, delaySec > 0 && anyLive, teamId || "league") || rawAll;
    // Ctrl+click multi-team / conference scope narrows the league schedule.
    const scopeIds = useMemo(() => (teamId ? null : (pwhlSurfMulti.length ? pwhlSurfMulti : (pwhlSurfGroup ? PWHL_CONFERENCES[pwhlSurfGroup] : null))), [teamId, pwhlSurfMulti, pwhlSurfGroup]);
    const allFixtures = useMemo(
        () => (allData || []).filter((f) => seasonList.includes(f.season) && f.date && (!scopeIds || scopeIds.includes(f.home_team_id) || scopeIds.includes(f.away_team_id))),
        [allData, seasonList, scopeIds],
    );
    const weeks = useMemo(() => buildCanonicalWeeks(allFixtures), [allFixtures]);

    // Per-game roster/lineup availability — used on BOTH the team schedule (one
    // greyable Roster/Lineup pair for the viewed team) and the league schedule
    // (a pair for EACH team, flanking the matchup).
    const { data: availList } = useQuery({
        queryKey: ["pwhl-game-availability"],
        queryFn: () => api.get("/pwhl/game-availability").then((r) => r.data),
        staleTime: 5 * 60 * 1000,
    });
    const availMap = useMemo(() => {
        const m = {};
        (availList || []).forEach((a) => { m[a.game_id] = a; });
        return m;
    }, [availList]);

    // Stories per week (design demo) — built from a light roster sample + the
    // full schedule, bucketed by Monday-week so each week (even empty ones) can
    // show its storylines beneath the games / "No games" bar.
    const { data: storyPlayers } = useQuery({
        queryKey: ["players", "schedule-stories"],
        queryFn: () => getPlayers({ limit: 24 }),
        staleTime: 10 * 60 * 1000,
    });
    const storiesWeekly = useMemo(
        () => buildStories(Array.isArray(storyPlayers) ? storyPlayers : (storyPlayers?.players || []), allFixtures, { catalog: settings?.stories_catalog }).weekly,
        [storyPlayers, allFixtures, settings?.stories_catalog],
    );

    const todayStr = new Date().toISOString().slice(0, 10);
    // "Next up" highlight = the soonest UPCOMING game; if the season is over /
    // offseason, fall back to the earliest game still unplayed (no final result)
    // so the next game to be played is always marked.
    const nextGameId = useMemo(() => {
        const notFinal = (f) => !(f.status === "Final" && f.home_score != null && f.away_score != null);
        const bySeq = (a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || ""));
        const upcoming = allFixtures.filter((f) => f.date >= todayStr && notFinal(f)).sort(bySeq);
        if (upcoming.length) return upcoming[0].id;
        // Never fall back to an old season's leftover unplayed game — use the latest season first.
        const unplayed = allFixtures.filter(notFinal).sort((a, b) => String(b.season).localeCompare(String(a.season)) || bySeq(a, b));
        return unplayed[0]?.id || null;
    }, [allFixtures, todayStr]);

    const [idx, setIdx] = useState(0);
    // NHL-style inline game-centre dropdown + responsive column count.
    const [openGameId, setOpenGameId] = useState(null);
    const [cols, setCols] = useState(5);
    // Extensions: reserve both side margins for the lineup boards — SAME maths as the NHL schedule.
    const extOnPage = getPref("page_toggle", "league_style") !== "pwhl" || getPref("page_toggle", "sched_side_lineups") !== "off";
    const [sideRes, setSideRes] = useState({ css: 0, vp: 0 });
    useEffect(() => {
        const calc = () => {
            const W = window.innerWidth;
            const pg = document.querySelector('[data-testid="pwhl-schedule-view"]');
            const pr = pg ? pg.getBoundingClientRect() : null;
            const eff = pg && pg.offsetWidth ? pr.width / pg.offsetWidth : 1;
            let extra = 0;
            let contentW = pg ? pg.offsetWidth : W;
            if (extOnPage && pg && sideGutterVp(W, openGameId != null) > 0) {
                const target = sideGutterVp(W, openGameId != null);
                extra = Math.round(Math.max(0, target - pr.left) / eff);
                contentW -= 2 * extra;
            }
            setSideRes({ css: extra, vp: Math.round(extra * eff) });
            const vis = contentW * eff;
            setCols(W >= 1024 ? (extOnPage && extra ? colsForWidth(vis) : 5) : extOnPage && extra ? colsForWidth(vis) : W >= 640 ? 2 : 1);
        };
        calc(); window.addEventListener("resize", calc);
        const t = setTimeout(calc, 300);
        return () => { window.removeEventListener("resize", calc); clearTimeout(t); };
    }, [extOnPage, openGameId != null]); // eslint-disable-line react-hooks/exhaustive-deps
    // Open on the week of the NEXT game to be played (soonest upcoming unplayed,
    // else the earliest unplayed after the last result) — NEVER the most-recently
    // played week. `nextGameId` already resolves that game.
    const weeksKey = weeks.length ? `${weeks[0].key}|${weeks[weeks.length - 1].key}|${weeks.length}` : "";
    // A pinned (favourited) season/stage always wins over the "next game" default.
    const pinSeason = getPref("page_toggle", `${legacyPath()}:pin:schedule-season`) || null;
    const pinStage = getPref("page_toggle", `${legacyPath()}:pin:schedule-stage-switcher`) || null;
    const pinApplied = useRef(false);
    // ---- NHL schedule mechanics (copied): frozen week bar + day headers, open game pins to the top ----
    const weekBarRef = useRef(null);
    const cardRefs = useRef({});
    useEffect(() => {
        let raf = 0;
        const apply = () => {
            const el = weekBarRef.current;
            if (el) document.documentElement.style.setProperty("--nhl-weekbar-h", `${el.offsetHeight || 58}px`);
            document.documentElement.style.setProperty("--nhl-hdr-bottom", `${topChromeBottom()}px`);
        };
        const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(apply); };
        apply();
        const ts = [400, 1200, 2500].map((ms) => setTimeout(apply, ms));
        const t = ts[0];
        const ro = (typeof ResizeObserver !== "undefined" && weekBarRef.current) ? new ResizeObserver(apply) : null;
        if (ro) ro.observe(weekBarRef.current);
        window.addEventListener("resize", apply);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => { ts.forEach(clearTimeout); void t; cancelAnimationFrame(raf); if (ro) ro.disconnect(); window.removeEventListener("resize", apply); window.removeEventListener("scroll", onScroll); };
    }, [weeks.length]);
    useEffect(() => {
        if (openGameId == null) return undefined;
        const desktop = window.innerWidth >= 1024;
        let cancelPin = () => {};
        const raf = requestAnimationFrame(() => { cancelPin = pinScheduleCard(cardRefs.current[openGameId], { desktop }); });
        return () => { cancelAnimationFrame(raf); cancelPin(); };
    }, [openGameId]);
    // Mobile: an open game and the Page Toggles bar never show together (opening one closes the other).
    useEffect(() => { window.dispatchEvent(new Event(openGameId ? "rosterly:game-open" : "rosterly:game-close")); }, [openGameId]);
    useEffect(() => {
        const close = () => setOpenGameId(null);
        window.addEventListener("rosterly:ctrl-open", close);
        return () => { window.removeEventListener("rosterly:ctrl-open", close); window.dispatchEvent(new Event("rosterly:game-close")); };
    }, []);
    // Switching games: close first, then open — so the pin always runs from a clean layout (NHL toggleGame).
    const toggleGame = (id) => {
        if (openGameId === id) { setOpenGameId(null); return; }
        if (openGameId != null) { setOpenGameId(null); setTimeout(() => setOpenGameId(id), 60); return; }
        // Mobile: the card jumps to the top THE MOMENT it's pressed (then the panel loads under it).
        const el = cardRefs.current[id];
        if (el && window.innerWidth < 1024) window.scrollTo(0, Math.max(0, el.getBoundingClientRect().top + window.scrollY - 8));
        setOpenGameId(id);
    };
    // SEO: the open game lives in the address bar (?game=ID&matchup=…); closing it restores the schedule URL.
    const gcOpened = useRef(false);
    useEffect(() => {
        if (openGameId == null) { if (gcOpened.current) syncGameUrl(null); return; }
        gcOpened.current = true;
        const f = allFixtures.find((x) => x.id === openGameId);
        syncGameUrl(openGameId, f ? { away: TEAM_INFO[f.away_team_id]?.name || f.away_team_id, home: TEAM_INFO[f.home_team_id]?.name || f.home_team_id, league: "PWHL", date: f.date } : {});
    }, [openGameId, allFixtures]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => () => { if (gcOpened.current) syncGameUrl(null); }, []);
    // Season + stage pins are applied TOGETHER here (never one at a time against a stale season).
    const togglePin = (id, cur, v) => setPref("page_toggle", `${legacyPath()}:pin:${id}`, cur === v ? null : v);
    useEffect(() => {
        if (!weeks.length) return;
        if (pinSeason && weeks.some((w) => w.season === pinSeason)) {
            if (!pinApplied.current) { pinApplied.current = true; jumpToTarget(pinSeason, pinStage || "regular"); }
            return;
        }
        if (pinApplied.current) return;
        if (pinStage && !pinSeason) {
            const ng = weeks.find((w) => w.games.some((g) => g.id === nextGameId)) || weeks[weeks.length - 1];
            pinApplied.current = true;
            jumpToTarget(ng.season, pinStage);
            return;
        }
        if (nextGameId) {
            const ng = allFixtures.find((f) => f.id === nextGameId);
            if (ng) {
                const nk = weekInfo(ng.date).key;
                let ni = weeks.findIndex((w) => w.key === nk && w.games.some((g) => g.id === ng.id));
                if (ni < 0) ni = weeks.findIndex((w) => w.key >= nk);
                if (ni >= 0) { setIdx(ni); return; }
            }
        }
        // No unplayed games anywhere → present week, else the last week with games.
        const tKey = weekInfo(todayStr).key;
        let i = weeks.findIndex((w) => w.key >= tKey);
        if (i < 0) i = weeks.length - 1;
        setIdx(i);
    }, [weeksKey, nextGameId, pinSeason, pinStage]); // eslint-disable-line react-hooks/exhaustive-deps
    // Deep-link: /teams?tab=schedule&date=YYYY-MM-DD jumps to the week holding
    // that game (used by the "PWHL Schedule" back link from a Game Centre).
    const jumpedRef = useRef(false);
    useEffect(() => {
        if (jumpedRef.current || !weeks.length) return;
        // ?game=ID (game notifications): jump to that game's week and open it.
        const qg = new URLSearchParams(window.location.search).get("game");
        const gw = qg ? weeks.findIndex((w) => w.games.some((g) => String(g.id) === qg)) : -1;
        if (gw >= 0) { setIdx(gw); setOpenGameId(weeks[gw].games.find((g) => String(g.id) === qg).id); jumpedRef.current = true; return; }
        const qd = new URLSearchParams(window.location.search).get("date");
        if (!qd) return;
        let i = weeks.findIndex((w) => w.key === weekInfo(qd).key && w.games.some((g) => String(g.date).slice(0, 10) === qd));
        if (i < 0) i = weeks.findIndex((w) => w.key === weekInfo(qd).key);
        if (i >= 0) { setIdx(i); jumpedRef.current = true; }
    }, [weeksKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const cur = weeks[idx] || null;
    const curSeason = cur?.season || "";
    const curStage = cur?.stage || "regular";

    // Keep the "Sort by Team" pill strip in sync with the VIEWED week's season.
    useEffect(() => {
        if (curSeason) setTeamIds(orderedTeamIdsForSeason(curSeason));
        return () => setTeamIds(null);
    }, [curSeason, setTeamIds]);

    // Selecting a season/stage jumps to the week holding the NEXT game to be
    // played in that season+stage (soonest unplayed / upcoming). Switching season
    // preserves the current stage (26-27 RS → 25-26 RS). Fallbacks: if that
    // stage has no unplayed games, land on the first week with games in it; if
    // the stage doesn't exist in the target season, land on that season's start.
    const jumpToTarget = (season, stage) => {
        const notFinal = (g) => !(g.status === "Final" && g.home_score != null && g.away_score != null);
        // Month view: land on the first month of this season+stage that still has
        // an unplayed game (else its first month) — the "next up" month.
        const stageMonths = monthsForView.map((m, mi) => ({ m, mi })).filter(({ m }) => m.season === season && m.stage === stage);
        if (stageMonths.length) {
            let mt = stageMonths.find(({ m }) => m.games.some(notFinal)) || stageMonths[0];
            setMonthIdx(mt.mi);
        } else {
            const anySeason = monthsForView.findIndex((m) => m.season === season);
            if (anySeason >= 0) setMonthIdx(anySeason);
        }
        const seg = weeks.map((w, i) => ({ w, i })).filter(({ w }) => w.season === season && w.stage === stage && w.games.length);
        if (!seg.length) {
            let i = weeks.findIndex((w) => w.season === season && w.games.length);
            if (i < 0) i = weeks.findIndex((w) => w.season === season);
            if (i >= 0) setIdx(i);
            return;
        }
        let target = seg.find(({ w }) => w.games.some((g) => g.date >= todayStr && notFinal(g)));
        if (!target) target = seg.find(({ w }) => w.games.some(notFinal));
        if (!target) target = seg[0];
        setIdx(target.i);
    };
    const jumpToSeason = (s) => jumpToTarget(s, curStage);
    const jumpToStage = (st) => jumpToTarget(curSeason, st);
    const jumpToday = () => { const tKey = weekInfo(todayStr).key; let i = weeks.findIndex((w) => w.key >= tKey); if (i < 0) i = weeks.length - 1; setIdx(Math.max(0, i)); };
    // Day | Week | Month (same toggles as the NHL schedule). Day/Month step through dates that have games.
    const range = getPref("page_toggle", "sched_range") || (typeof window !== "undefined" && window.innerWidth < 768 ? "day" : "week");
    const allGames = useMemo(() => weeks.flatMap((w) => w.games || []), [weeks]);
    const gameDates = useMemo(() => [...new Set(allGames.map((g) => g.date).filter(Boolean))].sort(), [allGames]);
    const gameMonths = useMemo(() => [...new Set(gameDates.map((d) => d.slice(0, 7)))], [gameDates]);
    const [selDate, setSelDate] = useState(null);
    const anchorDate = selDate || (cur?.games || []).map((g) => g.date).filter(Boolean).sort()[0] || gameDates[0] || "";
    const dIdx = Math.max(0, gameDates.findIndex((d) => d >= anchorDate));
    const mIdx = Math.max(0, gameMonths.indexOf((gameDates[dIdx] || "").slice(0, 7)));
    const goDate = (d) => { if (!d) return; setSelDate(d); const i = weeks.findIndex((w) => (w.games || []).some((g) => g.date === d)); if (i >= 0) setIdx(i); };
    // Mobile Day view uses the NHL-style bottom day strip: any calendar day, even with no games.
    const mobDay = typeof window !== "undefined" && window.innerWidth < 768 && range === "day";
    const stripDay = selDate || todayStr;
    const gamesOnDay = useMemo(() => allGames.reduce((m, g) => { if (g.date) m[g.date] = (m[g.date] || 0) + 1; return m; }, {}), [allGames]);
    const pickStripDay = (d) => { setSelDate(d); const i = weeks.findIndex((w) => (w.games || []).some((g) => g.date >= d)); if (i >= 0) setIdx(i); window.scrollTo({ top: 0 }); };
    // Desktop timeline: any calendar day / week / month can be picked (empty ones show "No games").
    const goAny = (d) => { if (!d) return; setSelDate(d); const k = weekInfo(d).key; let i = weeks.findIndex((w) => w.key >= k); if (i < 0) i = weeks.length - 1; setIdx(Math.max(0, i)); };
    const dayIso = selDate || gameDates[dIdx] || todayStr;
    const monKey = dayIso.slice(0, 7);
    const view = mobDay ? { ...cur, games: allGames.filter((g) => g.date === stripDay) }
        : range === "day" ? { ...cur, games: allGames.filter((g) => g.date === dayIso) }
        : range === "month" ? { ...cur, games: allGames.filter((g) => (g.date || "").startsWith(monKey)) } : cur;
    const navLabel = range === "day" ? _dLong(dayIso) : range === "month" ? `${FULL_MONTHS[Number(monKey.slice(5, 7)) - 1]} ${monKey.slice(0, 4)}` : `Week of ${cur?.key ? _dLong(cur.key) : "…"}`;
    const tlAnchor = range === "week" && cur?.key && cur.key !== "tbd" ? cur.key : range === "month" ? `${monKey}-01` : dayIso;
    const curEmpty = !!view && (view.games || []).length === 0;
    const prevGamesIdx = useMemo(() => { for (let i = idx - 1; i >= 0; i--) if (weeks[i]?.games.length) return i; return -1; }, [idx, weeks]);
    const nextGamesIdx = useMemo(() => { for (let i = idx + 1; i < weeks.length; i++) if (weeks[i]?.games.length) return i; return -1; }, [idx, weeks]);

    // ---- MONTH VIEW ----------------------------------------------------------
    // ONE continuous month timeline across the WHOLE schedule (every season +
    // stage), so the ◀▶ arrows can always walk to an adjacent month and never
    // dead-end on a single-month stage (e.g. a preseason that only spans one
    // month). Each page is a single (month · season · stage) bucket. The header
    // Season/Stage pills still quick-jump the week, which re-syncs the month.
    const monthsForView = useMemo(() => {
        const map = new Map();
        allFixtures
            .filter((f) => f.date)
            .forEach((f) => {
                const d = parseLocalDate(f.date);
                if (!d) return;
                const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                const stage = f.stage || "regular";
                const key = `${mk}|${f.season}|${stage}`;
                if (!map.has(key)) map.set(key, { key, monthKey: mk, label: `${FULL_MONTHS[d.getMonth()]} ${d.getFullYear()}`, season: f.season, stage, games: [] });
                map.get(key).games.push(f);
            });
        const rank = { preseason: 0, regular: 1, playoffs: 2 };
        return [...map.values()].sort((a, b) => a.monthKey.localeCompare(b.monthKey) || ((rank[a.stage] ?? 1) - (rank[b.stage] ?? 1)));
    }, [allFixtures]);
    const [monthIdx, setMonthIdx] = useState(0);
    // Stable content key — `monthsForView` gets a fresh array identity on every
    // render (useSeasonList sorts a new array each time), so depending on the raw
    // memo re-fired this effect EVERY render and reset the month back to default,
    // making the ◀▶ month arrows appear frozen. Keying on the actual month set +
    // the current week (season/stage) means we only re-sync on a real change.
    const monthsKey = monthsForView.map((m) => m.key).join("|");
    const monthInited = useRef(false);
    useEffect(() => {
        if (!monthsForView.length || monthInited.current) return;
        if ((pinSeason && monthsForView.some((m) => m.season === pinSeason)) || (pinStage && !pinSeason)) { monthInited.current = true; return; }
        const notFinal = (g) => !(g.status === "Final" && g.home_score != null && g.away_score != null);
        let i = -1;
        // Open on the MONTH of the next game to be played.
        if (nextGameId) {
            const ng = allFixtures.find((f) => f.id === nextGameId);
            const nd = ng ? parseLocalDate(ng.date) : null;
            if (nd) {
                const mk = `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}`;
                i = monthsForView.findIndex((m) => m.monthKey === mk && m.season === ng.season && m.stage === (ng.stage || "regular"));
                if (i < 0) i = monthsForView.findIndex((m) => m.monthKey === mk);
            }
        }
        // No unplayed games → first month with any unplayed, else present month, else last.
        if (i < 0) i = monthsForView.findIndex((m) => m.games.some(notFinal));
        if (i < 0) { const t = todayStr.slice(0, 7); i = monthsForView.findIndex((m) => m.monthKey >= t); }
        if (i < 0) i = monthsForView.length - 1;
        setMonthIdx(i);
        monthInited.current = true;
    }, [monthsKey, nextGameId]); // eslint-disable-line react-hooks/exhaustive-deps
    const curMonth = monthsForView[monthIdx] || null;


    // Mobile: Season/Stage jumps live in the bottom context bar (thumb zone).
    const STAGE_OPTS = [{ k: "preseason", l: "PRE" }, { k: "regular", l: "RS" }, { k: "playoffs", l: "PO" }];
    useMobileSubtabs(!teamId ? null : <MobilePageControls groups={[
        { id: "season", label: "Season", currentLabel: shortSeason(curSeason), options: seasonList.map((s) => ({ k: s, l: shortSeason(s), active: curSeason === s, onSelect: () => jumpToSeason(s) })) },
        { id: "stage", label: "Stage", currentLabel: (STAGE_OPTS.find((o) => o.k === curStage)?.l) || curStage, options: STAGE_OPTS.map((o) => ({ k: o.k, l: o.l, active: curStage === o.k, onSelect: () => jumpToStage(o.k) })) },
    ]} />, [curSeason, curStage, weeksKey, teamId]);

    const controlsBar = (
        <div className="hidden lg:flex items-center gap-2 flex-wrap justify-center w-full" data-testid="schedule-controls-desktop">
            {!teamId && <NavPageTitle page="Schedule" testId="schedule-league-title" />}
            <SeasonSwitcher value={curSeason} onChange={jumpToSeason} testIdPrefix="schedule" pinnedKey={pinSeason} onPin={(v) => togglePin("schedule-season", pinSeason, v)} />
            <SegmentedControl
                testId="schedule-stage-switcher"
                value={curStage}
                onChange={jumpToStage}
                pinnedKey={pinStage}
                onPin={(v) => togglePin("schedule-stage-switcher", pinStage, v)}
                options={[
                    { k: "preseason", l: "PRE", testId: "schedule-stage-preseason" },
                    { k: "regular", l: "RS", testId: "schedule-stage-regular" },
                    { k: "playoffs", l: "PO", testId: "schedule-stage-playoffs" },
                ]}
            />
        </div>
    );
    // Move the desktop filter bar up into the site nav (2nd/3rd nav bar), like Standings.
    const { setNav } = useGameNav();
    useEffect(() => {
        // Same as the NHL schedule: no page bar on the league schedule (team schedules keep Season / Stage).
        setNav(teamId ? { content: controlsBar } : null);
        return () => setNav(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [curSeason, curStage, teamId, weeks.length, idx]);

    return (
        <ScheduleBusinessCtx.Provider value={{ businessMode, biz, setGameRevenue, setStageDefault }}>
        <div className="space-y-4" data-testid="pwhl-schedule-view" style={sideRes.css ? { paddingLeft: sideRes.css, paddingRight: sideRes.css } : undefined}>
            {teamId && businessMode && <TeamRevenueSummary teamId={teamId} fixtures={allFixtures} biz={biz} season={curSeason} stage={curStage} />}
            {isLoading ? <Loader /> : !weeks.length ? (
                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-10 text-center" data-testid="pwhl-schedule-empty">
                    <CalendarDays className="mx-auto mb-3 text-[#9D4CDD]" size={26} />
                    <p className="text-sm text-white/70">No fixtures scheduled yet.</p>
                </div>
            ) : (
                <>
                    {mobDay && <DayStrip curDay={stripDay} todayIso={todayStr} onPick={pickStripDay} gamesOnDay={gamesOnDay} prefix="pwhl-sched" />}
                    <div className={mobDay ? "hidden" : "contents"}>
                    <PwhlWeekNav
                        ref={weekBarRef}
                        label={navLabel}
                        unit={range === "day" ? "Day" : range === "month" ? "Month" : "Week"}
                        onPrev={() => (range === "day" ? goDate(gameDates[dIdx - 1]) : range === "month" ? goDate(gameDates.find((d) => d.startsWith(gameMonths[mIdx - 1] || "~"))) : (setSelDate(null), setIdx((i) => Math.max(0, i - 1))))}
                        onNext={() => (range === "day" ? goDate(gameDates[dIdx + 1]) : range === "month" ? goDate(gameDates.find((d) => d.startsWith(gameMonths[mIdx + 1] || "~"))) : (setSelDate(null), setIdx((i) => Math.min(weeks.length - 1, i + 1))))}
                        prevDisabled={range === "day" ? dIdx <= 0 : range === "month" ? mIdx <= 0 : idx <= 0}
                        nextDisabled={range === "day" ? dIdx >= gameDates.length - 1 : range === "month" ? mIdx >= gameMonths.length - 1 : idx >= weeks.length - 1}
                        onToday={() => { setSelDate(null); jumpToday(); }}
                        delaySec={delaySec}
                        timeline={<ScheduleTimelineBar prefix="pwhl-sched" range={range} anchor={tlAnchor} todayIso={todayStr} counts={gamesOnDay} label={navLabel} onGo={goAny} delaySec={delaySec}
                            jump={{ seasons: seasonList.map((x) => ({ k: x, l: shortSeason(x), on: x === curSeason })), onSeason: (x) => { setSelDate(null); jumpToSeason(x); },
                                stages: [["preseason", "Preseason"], ["regular", "Regular"], ["playoffs", "Playoffs"]].map(([k, l]) => ({ k, l, on: k === curStage, disabled: !weeks.some((w) => w.season === curSeason && w.stage === k && w.games.length) })),
                                onStage: (x) => { setSelDate(null); jumpToStage(x); } }} />}
                    />
                    </div>
                    {(!view || curEmpty) ? (
                        <div className={`py-16 text-center text-white/50 ${mobDay ? "pb-44" : ""}`} data-testid="pwhl-sched-empty-week">{mobDay ? "No PWHL games this day." : "No games this week. Use Next Week to jump ahead."}</div>
                    ) : (
                        <div className={mobDay ? "pb-40" : undefined}><PwhlDayGrid week={view} cols={cols} sideReserveVp={sideRes.vp} openGameId={openGameId} toggleGame={toggleGame} cardRefs={cardRefs} nextGameId={nextGameId} /></div>
                    )}
                </>
            )}
        </div>
        </ScheduleBusinessCtx.Provider>
    );
}

// ===================== NHL-STYLE WEEK BOARD (PWHL) =====================
// Mirrors pages/NhlSchedule.jsx: prev/next week nav, day-grouped grid of compact
// game cards, and an inline "Game Centre" dropdown that expands beneath the row.
// Pre-game Game Centre — same as the NHL: a short note, the season series row, Close game.
function PwhlPregame({ f, all, recs, pwhl, onClose }) {
    const A = pwhl(f.away_team_id), H = pwhl(f.home_team_id);
    const pair = new Set([String(f.away_team_id), String(f.home_team_id)]);
    const series = all.filter((x) => x.season === f.season && x.stage === f.stage && pair.has(String(x.home_team_id)) && pair.has(String(x.away_team_id)))
        .sort((a, b) => `${a.date}${a.time || ""}`.localeCompare(`${b.date}${b.time || ""}`));
    const tile = (t, id) => (
        <div className="shrink-0 w-[64px] rounded-lg border flex flex-col items-center justify-center gap-0.5 py-1.5" style={{ borderColor: `${t.primary_color || "#475569"}80`, background: `${t.primary_color || "#475569"}22` }} data-testid={`pwhl-pre-team-${id}`}>
            <TeamBadge teamId={id} size="sm" asLink={false} />
            <span className="font-mono-stat text-[10px] font-black text-white">{recs?.[id] || "0-0-0-0"}</span>
        </div>
    );
    return (
        <div className="space-y-4" data-testid={`pwhl-pregame-${f.id}`}>
            <p className="text-center text-sm text-white/60 py-4">This game hasn't started yet. Scoring, player stats and play-by-play appear once it's underway.</p>
            <div className="flex items-stretch gap-1.5" data-testid="pwhl-pre-series">
                {tile(A, f.away_team_id)}
                <div className="flex-1 min-w-0 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.max(1, series.length)}, minmax(0, 1fr))` }}>
                    {series.map((x) => {
                        const done = x.away_score != null && x.home_score != null;
                        const winId = done ? (x.home_score > x.away_score ? x.home_team_id : x.away_team_id) : null;
                        const wc = winId ? pwhl(winId).primary_color || "#475569" : null;
                        const cur = x.id === f.id;
                        return (
                            <div key={x.id} className={`rounded-lg border overflow-hidden ${cur ? "border-white/40" : "border-white/10"}`} style={wc ? { borderColor: `${wc}99`, background: `${wc}26` } : undefined} data-testid={`pwhl-pre-series-${x.id}`}>
                                <div className="flex items-center justify-between px-1.5 py-0.5 bg-black/20 text-[8px] font-black uppercase tracking-wider text-white/60">
                                    <span>{new Date(`${x.date}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}</span>
                                    <span>{done ? `Final${x.shootout ? "/SO" : x.overtime ? "/OT" : ""}` : "Upcoming"}</span>
                                </div>
                                <div className="flex items-center justify-center gap-1.5 py-1">
                                    <TeamBadge teamId={x.away_team_id} size="xs" asLink={false} />
                                    <span className="font-mono-stat text-[10px] font-black text-white">{done ? `${x.away_score}–${x.home_score}` : "vs"}</span>
                                    <TeamBadge teamId={x.home_team_id} size="xs" asLink={false} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                {tile(H, f.home_team_id)}
            </div>
            <button type="button" onClick={onClose} data-testid="pwhl-pre-close" className="w-full h-9 rounded-lg border border-white/15 bg-white/[0.04] text-[11px] font-black uppercase tracking-wider text-white/75 hover:text-white hover:bg-white/10 inline-flex items-center justify-center gap-2"><X className="h-3.5 w-3.5" />Close game</button>
        </div>
    );
}

const PwhlWeekNav = React.forwardRef(function PwhlWeekNav({ label, unit = "Week", onPrev, onNext, prevDisabled, nextDisabled, onToday, delaySec = 0, timeline = null }, ref) {
    return (
        <div ref={ref} data-nhl-weekbar className="flex items-center justify-between gap-3 mb-4 sticky top-[var(--nhl-hdr-bottom,0px)] z-30 lg:-mt-[64px] -mx-4 px-4 py-2 lg:py-3 bg-[#0A0B10] lg:border-b lg:border-white/10" data-testid="pwhl-week-nav">
            {timeline}
            <button type="button" data-testid="pwhl-sched-prev" disabled={prevDisabled} onClick={onPrev}
                className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-xs font-bold uppercase tracking-wider text-white/80 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronLeft className="h-4 w-4" /> <span className="hidden sm:inline">Prev {unit}</span>
            </button>
            <div className="lg:hidden flex flex-col items-center min-w-0">
                <button type="button" onClick={onToday} title="Jump to today"
                    className="text-sm font-black uppercase tracking-widest text-white text-center" data-testid="pwhl-sched-week">
                    {label}
                </button>
                {delaySec > 0 && (
                    <span className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-[#F5B301]/75" data-testid="pwhl-sched-delay-tag">
                        <span className="h-1 w-1 rounded-full bg-[#F5B301]/75" /> Delayed <span className="normal-case">{delaySec < 60 ? `${delaySec}s` : `${Math.floor(delaySec / 60)}:${String(delaySec % 60).padStart(2, "0")}`}</span>
                    </span>
                )}
            </div>
            <button type="button" data-testid="pwhl-sched-next" disabled={nextDisabled} onClick={onNext}
                className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-xs font-bold uppercase tracking-wider text-white/80 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed">
                <span className="hidden sm:inline">Next {unit}</span> <ChevronRight className="h-4 w-4" />
            </button>
        </div>
    );
});

const _dLong = (dateStr) => {
    const d = parseLocalDate(dateStr);
    return d ? d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : "Date TBD";
};

const PWHL_BROADCASTERS = [
    { name: "DAZN", logo: "/broadcasters/dazn.png" },
    { name: "Premier Sports", logo: "/broadcasters/premier.png" },
    { name: "Amazon Prime", logo: "/broadcasters/prime.jpeg" },
    { name: "YouTube", logo: "/broadcasters/youtube.png" },
];
const YT_LOGO = (PWHL_BROADCASTERS.find((b) => b.name === "YouTube")?.logo || "/broadcasters/youtube.png") + "?v=3";

// Canada / Czechia / Slovakia viewers must NOT see the YouTube broadcast (regional block).
const CANADA_TZ = new Set([
    "America/Toronto", "America/Montreal", "America/Vancouver", "America/Edmonton",
    "America/Winnipeg", "America/Halifax", "America/St_Johns", "America/Regina",
    "America/Moncton", "America/Glace_Bay", "America/Goose_Bay", "America/Blanc-Sablon",
    "America/Creston", "America/Dawson", "America/Dawson_Creek", "America/Fort_Nelson",
    "America/Inuvik", "America/Iqaluit", "America/Rankin_Inlet", "America/Resolute",
    "America/Whitehorse", "America/Yellowknife", "America/Cambridge_Bay",
    "America/Rainy_River", "America/Thunder_Bay", "America/Nipigon",
    "America/Pangnirtung", "America/Atikokan", "America/Swift_Current",
]);
const _pwhlYtBlocked = () => {
    try {
        if (localStorage.getItem("pwhl_yt_block_preview") === "1") return true;
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (CANADA_TZ.has(tz) || tz === "Europe/Prague" || tz === "Europe/Bratislava") return true;
        const langs = [navigator.language, ...(navigator.languages || [])].filter(Boolean).map((l) => l.toLowerCase());
        for (const l of langs) {
            if (l.endsWith("-ca") || l.endsWith("-cz") || l.endsWith("-sk") || l === "cs" || l === "sk") return true;
        }
        return false;
    } catch { return false; }
};

// PWHL games always stream on YouTube (auto-filled for everyone) — except in the
// regionally-blocked countries (Canada / Czechia / Slovakia), where nothing shows.
function PwhlBroadcasterInline() {
    if (_pwhlYtBlocked()) return null;
    return (
        <span className="inline-flex items-center gap-1.5" data-testid="pwhl-sched-broadcast-youtube">
            <img src={YT_LOGO} alt="YouTube" title="Watch on YouTube" className="h-9 w-auto object-contain block" />
        </span>
    );
}


// Your saved PWHL game picks (any season) → fixture_id → pick + points (NHL scoring 3/2/1).
export function usePwhlPicks(season, enabled) {
    const { data } = useQuery({ queryKey: ["pwhl-game-preds", season], queryFn: () => getGamePredictions(season), enabled: enabled && !!season, staleTime: 60 * 1000 });
    return useMemo(() => {
        const m = {};
        (data?.predictions || []).forEach((p) => {
            const pts = p.winner_correct == null ? null : (p.winner_correct && p.score_correct ? 3 : p.winner_correct ? 2 : p.result_correct ? 1 : 0);
            m[p.fixture_id] = { mode: p.predict_score ? "score" : "quick", winner: p.predicted_winner_id, result: (p.predicted_result || "REG").toUpperCase(),
                home_score: p.predicted_home_score, away_score: p.predicted_away_score, points: pts };
        });
        return m;
    }, [data]);
}

function PwhlGameCard({ f, open, onToggle, bold, predict = null, compact = false, recs = null, opener = false, favColor = null, size = "big" }) {
    const bcOn = usePagePrefs().getPref("page_toggle", "broadcast_on") === "on";
    useTimePrefs();
    const { pwhl } = useTeamLookup();
    const away = pwhl(f.away_team_id), home = pwhl(f.home_team_id);
    const ev = opener ? homeOpenerEvent(home.primary_color) : null;
    const gHref = gameHref(window.location.pathname, f.id, away.name || away.abbr, home.name || home.abbr);
    const { auxOpen, auxDown } = auxHandlers(gHref);
    const seoA = <a href={gHref} className="sr-only" tabIndex={-1} onClick={(e) => e.preventDefault()} data-testid={`game-link-${f.id}`}>{away.name || away.abbr} vs {home.name || home.abbr}</a>;
    const evStyle = eventCardStyle(ev, open) || (favColor ? { background: `linear-gradient(135deg, ${favColor}59, ${favColor}1A 70%)`, borderColor: open ? "#FFFFFFAA" : `${favColor}AA` } : undefined);
    const isFinal = f.status === "Final";
    const isLive = f.status === "Live";
    const label = isFinal ? "Final" : isLive ? "Live" : formatFixtureTime(f.date, f.time, { noZone: true });
    const endTag = isFinal ? (f.shootout ? "SO" : f.overtime ? (f.num_ot > 1 ? `OT${f.num_ot}` : "OT") : "") : "";
    const pOn = !!predict?.enabled && !isFinal && !isLive;
    const init = predict?.initial || null;
    const [pWinner, setPWinner] = useState(init?.winner || "");
    const [pResult, setPResult] = useState((init?.result || "REG").toUpperCase());
    const [pMode, setPMode] = useState(init?.mode === "score" ? "score" : "quick");
    const [pHs, setPHs] = useState(init?.mode === "score" && init?.home_score != null ? String(init.home_score) : "");
    const [pAs, setPAs] = useState(init?.mode === "score" && init?.away_score != null ? String(init.away_score) : "");
    const firstSave = useRef(true);
    const h = parseInt(pHs, 10), a = parseInt(pAs, 10);
    const bothScores = Number.isFinite(h) && Number.isFinite(a);
    const scoreWinner = bothScores && h !== a ? (h > a ? f.home_team_id : f.away_team_id) : "";
    const effWinner = pMode === "score" ? scoreWinner : pWinner;
    const diffTooBig = pMode === "score" && bothScores && Math.abs(h - a) > 1;
    useEffect(() => { if (diffTooBig && pResult !== "REG") setPResult("REG"); }, [diffTooBig, pResult]);
    useEffect(() => {
        if (!pOn) return;
        if (firstSave.current) { firstSave.current = false; return; }
        if (!effWinner) return;
        const t = setTimeout(() => predict.onSave({ winner: effWinner, result: pResult, mode: pMode, home_score: pMode === "score" && bothScores ? h : 0, away_score: pMode === "score" && bothScores ? a : 0 }), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effWinner, pResult, pMode, pHs, pAs, pOn]);
    const pickTeam = (teamId) => {
        const hasRealScore = pMode === "score" && (pHs !== "" || pAs !== "") && !(parseInt(pHs || "0", 10) === 0 && parseInt(pAs || "0", 10) === 0);
        if (hasRealScore) return;
        setPMode("quick"); setPWinner(teamId); setPHs(""); setPAs("");
    };
    const typeScore = (which, v) => { setPMode("score"); setPWinner(""); if (which === "home") setPHs(v); else setPAs(v); };
    const focusScore = () => { if (pMode === "quick") { setPMode("score"); setPHs(""); setPAs(""); } };
    const clearPred = () => { setPWinner(""); setPHs(""); setPAs(""); setPMode("quick"); setPResult("REG"); predict.onClear(); };
    const pcode = (teamId) => { if (!effWinner) return ""; const won = teamId === effWinner; if (pResult === "OT") return won ? "OTW" : "OTL"; if (pResult === "SO") return won ? "SOW" : "SOL"; return won ? "W" : "L"; };
    const Row = (t, teamId, score, won, which) => {
        const isWin = pOn && effWinner === teamId;
        const rowProps = pOn ? { role: "button", tabIndex: -1, "data-testid": `pwhl-predict-pick-${(t.abbr || "").toUpperCase()}`, onClick: (e) => { e.stopPropagation(); e.preventDefault(); pickTeam(teamId); } } : {};
        return (
            <div className={`flex items-center gap-2.5 min-w-0 rounded-md ${pOn ? "cursor-pointer -mx-1 px-1 -my-0.5 py-0.5" : ""} ${isWin ? "bg-[#3B82F6]/10 ring-1 ring-[#3B82F6]/40" : ""}`} {...rowProps}>
                <span className="h-8 w-8 shrink-0 flex items-center justify-center">
                    {t.logo_url ? <img src={assetUrl(t.logo_url)} alt={t.abbr} className="h-8 w-8 object-contain" /> : <span className="text-[10px] font-black text-white/70">{(t.abbr || "").toUpperCase()}</span>}
                </span>
                <span className="min-w-0 flex flex-col leading-tight">
                    <span className={`text-sm font-bold truncate ${isWin ? "text-white" : pOn ? "text-white/65" : (isFinal && !won ? "text-white/55" : "text-white")}`}>{t.name || t.abbr}</span>
                    {recs?.real?.[teamId] && (
                        <span className="text-[9px] font-semibold tabular-nums" data-testid={`pwhl-rec-${f.id}-${which}`}>
                            <span className="text-white/35" title="Real record (W-OTW-OTL-L)">{recs.real[teamId]}</span>
                            {predict?.enabled && recs.at?.[f.id]?.[teamId] && <span className="block text-[#93C5FD]/80" data-testid={`pwhl-pred-rec-${f.id}-${which}`} title="Predicted record after this game (W-OTW-OTL-L)">{recs.at[f.id][teamId]}</span>}
                        </span>
                    )}
                </span>
                {pOn ? (
                    <input inputMode="numeric" value={pMode === "score" ? (which === "home" ? pHs : pAs) : pcode(teamId)}
                        onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onFocus={focusScore}
                        onChange={(e) => typeScore(which, e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                        data-testid={`pwhl-predict-box-${(t.abbr || "").toUpperCase()}`}
                        className="ml-auto w-10 h-7 rounded bg-black/70 border text-center text-sm font-black tabular-nums text-white focus:outline-none"
                        style={{ borderColor: isWin ? "#3B82F6" : "rgba(255,255,255,0.16)" }} />
                ) : ((isFinal || isLive) && <span className={`ml-auto text-base font-black tabular-nums ${isFinal && !won ? "text-white/40" : "text-white"}`}>{score ?? "–"}</span>)}
            </div>
        );
    };
    const awayWon = isFinal && f.away_score > f.home_score;
    const homeWon = isFinal && f.home_score > f.away_score;
    // COMPACT card (same as NHL): LOGO | AWAY | score–score | HOME | LOGO. Predict stays compact.
    if (size === "small") {
        const hasScore = (isFinal || isLive) && f.away_score != null && f.home_score != null;
        const side = (t, id) => (
            <span className="flex flex-col items-center gap-0.5 min-w-0">
                {t.logo_url ? <img src={assetUrl(t.logo_url)} alt={t.abbr} className="h-7 w-7 object-contain" /> : <b className="text-[10px] text-white">{t.abbr}</b>}
                {recs?.real?.[id] && <span className="text-[8px] font-semibold text-white/45 tabular-nums whitespace-nowrap">{recs.real[id]}</span>}
            </span>
        );
        return (
            <div role="button" onAuxClick={auxOpen} onMouseDown={auxDown} tabIndex={0} onClick={onToggle} data-testid={`fixture-${f.id}`} style={evStyle}
                className={`relative w-full rounded-lg border px-1.5 py-1.5 flex items-center justify-between gap-1 cursor-pointer transition-colors ${open ? "border-white/40 bg-white/[0.07]" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
                {seoA}
                {side(away, f.away_team_id)}
                <span className="flex flex-col items-center leading-none">
                    {hasScore ? <b className="text-[13px] font-black text-white tabular-nums">{f.away_score}-{f.home_score}</b> : <span className="text-[9px] font-black uppercase text-white/60 whitespace-nowrap">{label}</span>}
                    {hasScore && <span className={`mt-0.5 text-[7px] font-black uppercase tracking-widest ${isLive ? "text-[#EF4444]" : "text-white/40"}`}>{isLive ? "Live" : `Final${endTag ? `/${endTag}` : ""}`}</span>}
                </span>
                {side(home, f.home_team_id)}
            </div>
        );
    }
    if (compact) {
        const ab = (t) => (t.abbr || "").toUpperCase();
        const logo = (t) => (t.logo_url ? <img src={assetUrl(t.logo_url)} alt={t.abbr} className="h-6 w-6 object-contain shrink-0" /> : null);
        const pick = (t, teamId, won) => (pOn ? (
            <button type="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); pickTeam(teamId); }} data-testid={`pwhl-predict-pick-${ab(t)}`}
                className={`text-xs font-black uppercase tracking-wide rounded px-1 py-0.5 ${effWinner === teamId ? "text-white bg-[#3B82F6]/15 ring-1 ring-[#3B82F6]/50" : "text-white/55 hover:text-white"}`}>{ab(t)}</button>
        ) : <span className={`text-xs font-black uppercase tracking-wide ${won ? "text-white" : "text-white/70"}`}>{ab(t)}</span>);
        const box = (teamId, which) => (
            <input inputMode="numeric" value={pMode === "score" ? (which === "home" ? pHs : pAs) : pcode(teamId)}
                onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onFocus={focusScore}
                onChange={(e) => typeScore(which, e.target.value.replace(/[^0-9]/g, "").slice(0, 2))} data-testid={`pwhl-predict-box-${which}-${f.id}`}
                className="w-9 h-6 rounded bg-black/70 border text-center text-xs font-black tabular-nums text-white focus:outline-none" style={{ borderColor: effWinner === teamId ? "#3B82F6" : "rgba(255,255,255,0.16)" }} />
        );
        const hasScore = (isFinal || isLive) && f.away_score != null && f.home_score != null;
        return (
            <div role="button" onAuxClick={auxOpen} onMouseDown={auxDown} tabIndex={pOn ? -1 : 0} onClick={onToggle} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }} data-testid={`fixture-${f.id}`} style={evStyle} data-event={ev?.key}
                className={`relative w-full rounded-lg border px-2.5 py-2 flex flex-col items-stretch gap-1.5 cursor-pointer transition-colors ${open ? "border-white/40 bg-white/[0.07]" : bold ? "border-[#9D4CDD]/40 hover:border-[#9D4CDD]/70" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
                {seoA}
                <EventTitle ev={ev} gid={f.id} />
                <div className="flex items-center justify-center gap-2">
                    {logo(away)}{pick(away, f.away_team_id, awayWon)}
                    <span className="flex items-center justify-center gap-1 min-w-[54px]">
                        {pOn ? <>{box(f.away_team_id, "away")}<span className="text-white/25">-</span>{box(f.home_team_id, "home")}</>
                            : hasScore ? <><span className={`tabular-nums font-black ${awayWon ? "text-white" : "text-white/55"}`}>{f.away_score}</span><span className="text-white/25">-</span><span className={`tabular-nums font-black ${homeWon ? "text-white" : "text-white/55"}`}>{f.home_score}</span>{endTag && <span className="ml-1 text-[9px] font-black text-white/35">{endTag}</span>}</>
                            : <span className="text-[10px] font-black uppercase tracking-wider text-white/45 whitespace-nowrap">{isLive ? "LIVE" : label}</span>}
                    </span>
                    {pick(home, f.home_team_id, homeWon)}{logo(home)}
                </div>
                {pOn && effWinner && (
                    <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <span className="inline-flex items-center rounded border border-white/12 overflow-hidden" data-testid={`pwhl-predict-rtype-${f.id}`}>
                            {["REG", "OT", "SO"].map((r) => {
                                const dis = diffTooBig && r !== "REG";
                                return <span key={r} role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); if (!dis) setPResult(r); }} className={`px-1.5 py-0.5 text-[9px] font-black uppercase ${dis ? "text-white/15" : pResult === r ? "text-white" : "text-white/40 hover:text-white"}`} style={pResult === r && !dis ? { background: "#3B82F633" } : undefined}>{r}</span>;
                            })}
                            <span role="button" tabIndex={-1} title="Clear prediction" onClick={(e) => { e.stopPropagation(); clearPred(); }} className="flex items-center px-1.5 leading-none text-[9px] font-black uppercase text-white/40 hover:text-white border-l border-white/12">✕</span>
                        </span>
                    </div>
                )}
            </div>
        );
    }
    // Locked (started/final) games keep showing YOUR pick + points earned — same as NHL.
    const lockedPick = !!predict?.enabled && (isFinal || isLive) && init?.winner ? init : null;
    return (
        <div role="button" onAuxClick={auxOpen} onMouseDown={auxDown} tabIndex={pOn ? -1 : 0} onClick={onToggle} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }} data-testid={`fixture-${f.id}`} style={evStyle} data-event={ev?.key}
            className={`relative w-full text-left rounded-xl border bg-white/[0.03] p-3.5 flex flex-col gap-2 transition-colors cursor-pointer ${open ? "border-white/40 bg-white/[0.07]" : bold ? "border-[#9D4CDD]/40 hover:border-[#9D4CDD]/70" : "border-white/10 hover:border-white/25"}`}>
            {seoA}
            <EventTitle ev={ev} gid={f.id} />
            {Row(away, f.away_team_id, f.away_score, awayWon, "away")}
            {Row(home, f.home_team_id, f.home_score, homeWon, "home")}
            {lockedPick && (
                <div className="flex items-center justify-between gap-2 rounded-md bg-[#3B82F6]/10 border border-[#3B82F6]/30 px-2 py-1" data-testid={`pwhl-locked-pick-${f.id}`}>
                    <span className="text-[10px] font-black uppercase tracking-wider text-[#93C5FD]">
                        Your pick · {(pwhl(lockedPick.winner).abbr || lockedPick.winner || "").toUpperCase()} {lockedPick.result !== "REG" ? lockedPick.result : ""}
                        {lockedPick.mode === "score" && (lockedPick.home_score || lockedPick.away_score) ? ` · ${lockedPick.away_score}-${lockedPick.home_score}` : ""}
                    </span>
                    {lockedPick.points != null && (
                        <span className={`text-[10px] font-black ${lockedPick.points >= 2 ? "text-[#22C55E]" : lockedPick.points === 1 ? "text-[#F6C453]" : "text-[#FB7185]"}`} data-testid={`pwhl-locked-pts-${f.id}`}>+{lockedPick.points} pt{lockedPick.points === 1 ? "" : "s"}</span>
                    )}
                </div>
            )}
            <div className="mt-1 flex items-center justify-between gap-2 border-t border-white/5 pt-2">
                <span className="inline-flex items-center gap-2 min-w-0 flex-wrap">
                    {pOn ? (
                        <span className="inline-flex items-stretch h-[15px] rounded border border-white/12 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid={`pwhl-predict-rtype-${f.id}`}>
                            {["REG", "OT", "SO"].map((r) => {
                                const dis = diffTooBig && r !== "REG";
                                return (
                                    <span key={r} role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); if (!dis) setPResult(r); }}
                                        title={dis ? "OT/SO only apply to 1-goal games" : r}
                                        className={`flex items-center px-1.5 leading-none text-[9px] font-black uppercase transition-colors ${dis ? "text-white/15 cursor-not-allowed" : pResult === r ? "text-white cursor-pointer" : "text-white/40 hover:text-white cursor-pointer"}`}
                                        style={pResult === r && !dis ? { background: "#3B82F633" } : undefined}>{r}</span>
                                );
                            })}
                            {effWinner && <span role="button" tabIndex={-1} title="Clear prediction" onClick={(e) => { e.stopPropagation(); clearPred(); }} className="flex items-center px-1.5 leading-none text-[9px] font-black uppercase text-white/40 hover:text-white border-l border-white/12 cursor-pointer">✕</span>}
                        </span>
                    ) : (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider ${isLive ? "text-[#22C55E]" : isFinal ? "text-[#FBBF24]" : "text-white/45"}`}>
                            {isLive && <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] animate-pulse" />}{label}
                            {endTag && <span className="ml-1 text-white/30">{endTag}</span>}
                        </span>
                    )}
                    {bcOn && !pOn && f.stage !== "preseason" && <PwhlBroadcasterInline />}
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/40 shrink-0">
                    {open ? "Hide" : "Game Centre"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </div>
        </div>
    );
}

function PwhlDayGrid({ week, cols, sideReserveVp = 0, openGameId, toggleGame, cardRefs, nextGameId }) {
    const { getPref } = usePagePrefs();
    // Account favourite teams (1st, 2nd…): team-colour cards + first in each day (Favourites toggle, on by default).
    const pwFavs = useAccountFavs().pwhl.map(String);
    const pwFavFirst = getPref("page_toggle", "sched_fav_first") !== "off";
    const { pwhl: pwTeam } = useTeamLookup();
    const pwFavColor = (g) => { const id = pwFavs[favRankOf(pwFavs, [String(g.home_team_id), String(g.away_team_id)])]; return id ? pwTeam(id)?.primary_color || null : null; };
    const { user } = useAuth();
    const predictOn = getPref("page_toggle", "sched_predict") === "on";
    const predRecPref = getPref("page_toggle", "pred_rec") !== "off";
    const compact = getPref("page_toggle", "sched_compact_cards") === "on";
    const cardSize = cardSizeOf(getPref, "sched");
    const sizeEff = cardSize === "small" && getPref("page_toggle", "sched_predict") === "on" ? "medium" : cardSize;
    const pwSpoil = getPref("page_toggle", "sched_spoilers") === "on";
    const hideScore = (g) => (pwSpoil && g.away_score != null ? { ...g, away_score: null, home_score: null, _spoiler: true } : g);
    const extOn = getPref("page_toggle", "sched_side_lineups") !== "off";
    const { data: openerSettings } = useQuery({ queryKey: ["settings"], queryFn: getSettings, staleTime: 5 * 60 * 1000 });
    const openersOn = true;
    const { data: leagueAll } = useQuery({ queryKey: ["pwhl-schedule-all", "league"], queryFn: () => api.get("/pwhl/schedule").then((r) => r.data), enabled: openersOn, staleTime: 10 * 60 * 1000 });
    // Each team's first regular-season HOME game, per season.
    const openerIds = useMemo(() => {
        const first = {};
        (leagueAll || []).forEach((f) => {
            if (f.stage !== "regular" || !f.date || !f.home_team_id) return;
            const k = `${f.season}|${f.home_team_id}`;
            const cur = first[k];
            if (!cur || `${f.date}${f.time || ""}` < `${cur.date}${cur.time || ""}`) first[k] = f;
        });
        return new Set(Object.values(first).map((f) => f.id));
    }, [leagueAll]);
    const { pwhl } = useTeamLookup();
    const sideRefs = useRef({});
    const [focusId, setFocusId] = useState(null);
    const openFx = (week.games || []).find((g) => g.id === focusId) || (week.games || []).find((g) => g.id === openGameId);
    const nhlStyle = getPref("page_toggle", "league_style") !== "pwhl";
    // NHL style = the NHL schedule's side lineups (any desktop window); PWHL style keeps its own boards.
    const sideOn = !!openFx && (nhlStyle ? typeof window !== "undefined" && sideBoardsFit(window.innerWidth) : extOn && sideReserveVp > 0);
    const predSeason = (week.games && week.games[0] && week.games[0].season) || "";
    const { data: predGrid } = useQuery({
        queryKey: ["predictions-grid", predSeason, "regular"],
        queryFn: () => getPredictionsGrid(predSeason, "regular"),
        enabled: (predictOn || predRecPref) && !!user && !!predSeason,
    });
    const savedPicks = usePwhlPicks(predSeason, predictOn && !!user);
    const predReady = !predictOn || !user || !!predGrid;
    // Styles access option (shared by every league): "nhl" → PWHL games use the NHL schedule card (past games too).
    const predRecOf = (f) => { const r = recs?.at?.[f.id]; return r ? { [pwhl(f.away_team_id).abbr]: r[f.away_team_id], [pwhl(f.home_team_id).abbr]: r[f.home_team_id] } : null; };
    const abbrOfId = (f, id) => pwhl(String(id) === String(f.away_team_id) ? f.away_team_id : f.home_team_id).abbr;
    const idOfAbbr = (f, abbr) => (pwhl(f.away_team_id).abbr === abbr ? f.away_team_id : f.home_team_id);
    const toNhlCard = (f) => {
        const live = /live|progress|period/i.test(f.status || ""), fin = !live && (/final/i.test(f.status || "") || f.away_score != null);
        const side = (id, score) => { const t = pwhl(id); return { abbr: t.abbr, name: t.name, logo_url: assetUrl(t.logo_url), score, record: recs?.real?.[id] || null }; };
        return { id: f.id, _raw: f.id, gameState: fin ? "FINAL" : live ? "LIVE" : "FUT", startTimeUTC: pwhlUtc(f.date, f.time), periodType: f.shootout ? "SO" : f.overtime ? "OT" : "REG", clock: {},
            away: side(f.away_team_id, fin || live ? f.away_score : undefined), home: side(f.home_team_id, fin || live ? f.home_score : undefined) };
    };
    const qcG = useQueryClient();
    // Real record per team; with Predict ON, also the predicted record THROUGH each predicted game (matches Predicted Standings).
    const { data: realStand } = useQuery({ queryKey: ["pwhl-sched-recs", predSeason], queryFn: () => api.get("/pwhl/standings/computed", { params: { season: predSeason, stage: "regular", split: "full" } }).then((r) => r.data), enabled: !!predSeason, staleTime: 60 * 1000 });
    const recs = useMemo(() => {
        if (!Array.isArray(realStand)) return null;
        const real = {};
        realStand.forEach((r) => { real[r.team_id] = recStr(r); });
        const at = {};
        if (predictOn || predRecPref) (predGrid?.teams || []).forEach((t) => {
            const c = { w: 0, otw: 0, otl: 0, l: 0 };
            [...(t.games || [])].sort((a, b) => (a.date || "").localeCompare(b.date || "")).forEach((g) => {
                // Same engine as Predicted Standings: only YOUR picks count, game by game.
                const code = g.predicted_code;
                if (!code) return;
                if (code === "W") c.w += 1; else if (code === "OTW" || code === "SOW") c.otw += 1; else if (code === "OTL" || code === "SOL") c.otl += 1; else if (code === "L") c.l += 1;
                (at[g.fixture_id] = at[g.fixture_id] || {})[t.team_id] = recStr(c);
            });
        });
        return { real, at };
    }, [realStand, predGrid, predictOn, predRecPref]);
    const picks = useMemo(() => {
        const m = {};
        (predGrid?.teams || []).forEach((t) => (t.games || []).forEach((g) => {
            if (m[g.fixture_id]) return;
            const code = g.predicted_code || "";
            if (!code) return;
            const win = ["W", "OTW", "SOW"].includes(code);
            m[g.fixture_id] = {
                mode: g.predicted_score ? "score" : "quick",
                winner: win ? t.team_id : g.opponent_id,
                home_score: g.predicted_home_score, away_score: g.predicted_away_score,
                result: code.includes("OT") ? "OT" : code.includes("SO") ? "SO" : "REG",
            };
        }));
        return { ...m, ...savedPicks };
    }, [predGrid, savedPicks]);
    const byDay = useMemo(() => {
        const m = new Map();
        [...week.games]
            .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")))
            .sort((a, b) => (a.date || "").localeCompare(b.date || "") || (pwFavFirst ? Number(favRankOf(pwFavs, [String(a.home_team_id), String(a.away_team_id)]) === 999) - Number(favRankOf(pwFavs, [String(b.home_team_id), String(b.away_team_id)]) === 999) : 0))
            .forEach((f) => { if (!m.has(f.date)) m.set(f.date, []); m.get(f.date).push(f); });
        return pastLast([...m.entries()], ([d]) => d || "", ([, gs]) => !gs.some((f) => /progress|live|intermission|period|\bot\b/i.test(f.status || "") && !/final/i.test(f.status || "")));
    }, [week, pwFavs.join(","), pwFavFirst]); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <div>
        {sideOn && (<>
            {nhlStyle ? (openFx.id === openGameId && <NhlApiBase.Provider value="/pwhlx">
                <SideLineupOverlay side="left" scrollRefs={sideRefs} reserve={sideReserveVp} anchor='[data-testid="pwhl-schedule-view"]'><NhlSideLineup gameId={openGameId} team={pwhl(openFx.away_team_id).abbr} name={pwhl(openFx.away_team_id).name} logoUrl={assetUrl(pwhl(openFx.away_team_id).logo_url)} sideLabel="Away" /></SideLineupOverlay>
                <SideLineupOverlay side="right" scrollRefs={sideRefs} reserve={sideReserveVp} anchor='[data-testid="pwhl-schedule-view"]'><NhlSideLineup gameId={openGameId} team={pwhl(openFx.home_team_id).abbr} name={pwhl(openFx.home_team_id).name} logoUrl={assetUrl(pwhl(openFx.home_team_id).logo_url)} sideLabel="Home" /></SideLineupOverlay>
            </NhlApiBase.Provider>) : <>
            <SideLineupOverlay side="left" scrollRefs={sideRefs} reserve={sideReserveVp} anchor='[data-testid="pwhl-schedule-view"]'><PwhlSideLineup team={{ ...pwhl(openFx.away_team_id), id: openFx.away_team_id }} sideLabel="Away" /></SideLineupOverlay>
            <SideLineupOverlay side="right" scrollRefs={sideRefs} reserve={sideReserveVp} anchor='[data-testid="pwhl-schedule-view"]'><PwhlSideLineup team={{ ...pwhl(openFx.home_team_id), id: openFx.home_team_id }} sideLabel="Home" /></SideLineupOverlay>
            </>}
        </>)}
        <div className="space-y-0 [&>div]:pb-7" data-testid="pwhl-sched-grid" style={{ overflowAnchor: "none" }}>
            {byDay.map(([date, games]) => {
                const openIdx = games.findIndex((g) => g.id === openGameId);
                const gcols = sizeEff === "small" ? (typeof window !== "undefined" && window.innerWidth < 768 ? 3 : 9) : cols;
                const rowEnd = openIdx >= 0 ? Math.min(games.length - 1, (Math.floor(openIdx / gcols) + 1) * gcols - 1) : -1;
                const colInRow = openIdx >= 0 ? openIdx % gcols : 0;
                return (
                    <div key={date} data-testid={`pwhl-sched-day-${date}`}>
                        <div data-nhl-dayhdr className="flex items-center gap-3 mb-3 lg:sticky lg:top-[calc(var(--nhl-hdr-bottom,180px)+var(--nhl-weekbar-h,58px))] lg:z-20 lg:-mx-4 lg:px-4 lg:py-2 lg:bg-[#0A0B10]">
                            <h2 className="text-lg font-black uppercase tracking-wide text-white">{_dLong(date)}</h2>
                            <span className="text-[11px] font-bold text-white/40">{games.length} game{games.length !== 1 ? "s" : ""}</span>
                            <span className="flex-1 h-px bg-white/10" />
                        </div>
                        <div className={sizeEff === "small" ? "grid grid-cols-3 md:grid-cols-9 gap-2" : `grid grid-cols-1 sm:grid-cols-2 ${cols === 3 ? "lg:grid-cols-3" : cols === 4 ? "lg:grid-cols-4" : "lg:grid-cols-5"} gap-3`}>
                            {games.map((g, i) => (
                                <React.Fragment key={g.id}>
                                    <div className="flex flex-col rounded-xl" ref={(el) => { if (el) cardRefs.current[g.id] = el; else delete cardRefs.current[g.id]; }}
                                        onPointerDownCapture={() => { if (extOn) setFocusId(g.id); }} onFocusCapture={() => { if (extOn) setFocusId(g.id); }}>
                                        {nhlStyle ? <NhlGameCard g={toNhlCard(hideScore(g))} predRec={predRecOf(g)} broadcasters={[]} size={sizeEff} compact={compact || cardSize === "small"} favColor={pwFavColor(g)} open={g.id === openGameId} onToggle={() => toggleGame(g.id)} opener={openersOn && openerIds.has(g.id) ? { primary: pwhl(g.home_team_id)?.primary_color } : null} linkPath="/pwhl/schedule"
                                            predict={predictOn && user && g.stage === "regular" ? {
                                                enabled: true, initial: picks[g.id] ? { ...picks[g.id], winner: abbrOfId(g, picks[g.id].winner) } : null,
                                                onSave: (p) => saveGamePrediction(g.id, { predicted_winner_id: idOfAbbr(g, p.winner), predicted_result: p.result, predict_score: p.mode === "score", predicted_home_score: p.home_score, predicted_away_score: p.away_score }).then(() => { toast.success("Prediction saved"); qcG.invalidateQueries({ queryKey: ["predictions-grid", predSeason, "regular"] }); }).catch(() => toast.error("Couldn't save prediction")),
                                                onClear: () => deleteGamePrediction(g.id).then(() => qcG.invalidateQueries({ queryKey: ["predictions-grid", predSeason, "regular"] })).catch(() => {}),
                                            } : null} /> :
                                        <PwhlGameCard key={predReady ? "ready" : "wait"} favColor={pwFavColor(g)} opener={openersOn && openerIds.has(g.id)} recs={recs} f={hideScore(g)} size={sizeEff} compact={compact || cardSize === "small"} bold={g.id === nextGameId} open={g.id === openGameId} onToggle={() => toggleGame(g.id)}
                                            predict={predictOn && user && g.stage === "regular" ? {
                                                enabled: true, initial: picks[g.id],
                                                onSave: (p) => saveGamePrediction(g.id, { predicted_winner_id: p.winner, predicted_result: p.result, predict_score: p.mode === "score", predicted_home_score: p.home_score, predicted_away_score: p.away_score }).then(() => { toast.success("Prediction saved"); qcG.invalidateQueries({ queryKey: ["predictions-grid", predSeason, "regular"] }); }).catch(() => toast.error("Couldn't save prediction")),
                                                onClear: () => deleteGamePrediction(g.id).then(() => qcG.invalidateQueries({ queryKey: ["predictions-grid", predSeason, "regular"] })).catch(() => {}),
                                            } : null} />}
                                    </div>
                                    {i === rowEnd && (nhlStyle || !(sideOn && predictOn)) && (
                                        <div className="col-span-full fade-up" data-testid={`pwhl-gc-rowpanel-${openGameId}`} style={typeof window !== "undefined" && window.innerWidth < 768 ? { minHeight: "calc(100dvh - 260px)" } : undefined}>
                                            <div className="relative mt-1 rounded-xl border border-white/10 bg-[#0E0F16] p-4 sm:p-5">
                                                <div className="hidden sm:block absolute -top-2 h-3.5 w-3.5 rotate-45 bg-[#0E0F16] border-l border-t border-white/10" style={{ left: `calc(${((colInRow + 0.5) / gcols) * 100}% - 7px)` }} />
                                                {(() => {
                                                    const og = (week.games || []).find((x) => x.id === openGameId);
                                                    const pre = og && og.away_score == null && !/final|live|progress|period/i.test(og.status || "");
                                                    if (nhlStyle) return <NhlApiBase.Provider value="/pwhlx"><NhlGameCentrePanel gameId={openGameId} onClose={() => toggleGame(openGameId)} hideLineupsTab={sideOn} /></NhlApiBase.Provider>;
                                                    return pre ? <PwhlPregame f={og} all={leagueAll || []} recs={recs?.real} pwhl={pwhl} onClose={() => toggleGame(openGameId)} />
                                                        : <GameCentre key={openGameId} gameIdProp={openGameId} embedded />;
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
        </div>
    );
}

export const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];export const FULL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const parseLocalDate = (s) => {
    const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
// Monday-anchored week key + label for a YYYY-MM-DD date.
export const weekInfo = (dateStr) => {
    const d = parseLocalDate(dateStr);
    if (!d) return { key: "tbd", label: "Date TBD" };
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const key = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
    const label = monday.getMonth() === sunday.getMonth()
        ? `${SHORT_MONTHS[monday.getMonth()]} ${monday.getDate()} – ${sunday.getDate()}`
        : `${SHORT_MONTHS[monday.getMonth()]} ${monday.getDate()} – ${SHORT_MONTHS[sunday.getMonth()]} ${sunday.getDate()}`;
    return { key, label };
};
const dayLabel = (dateStr) => {
    const d = parseLocalDate(dateStr);
    return d ? `${SHORT_DAYS[d.getDay()]} · ${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}` : "Date TBD";
};
export { dayLabel };

// Week label including the year (timeline spans multiple seasons).
export const weekLabelWithYear = (key) => {
    const wi = weekInfo(key);
    return wi.key === "tbd" ? wi.label : `${wi.label}, ${key.slice(0, 4)}`;
};

// Build ONE continuous canonical week timeline across ALL seasons + stages.
// Every Monday-week between the first and last scheduled game is present (even
// empty weeks). Each week is tagged with the season + stage it belongs to —
// derived from its games, then forward/back-filled across empty gaps so paging
// stays in true date order and the stage/season toggles can auto-switch.
export function buildCanonicalWeeks(list) {
    if (!list || !list.length) return [];
    const map = new Map();
    // Preseason and regular season never share a page: a Monday-week holding
    // both is split into one bucket per season+stage (same key, stage order).
    const stageRank = { preseason: 0, regular: 1, playoffs: 2 };
    for (const f of list) {
        const wi = weekInfo(f.date);
        if (wi.key === "tbd") continue;
        if (!map.has(wi.key)) map.set(wi.key, new Map());
        const sub = map.get(wi.key);
        const sk = `${f.season}|${f.stage || "regular"}`;
        if (!sub.has(sk)) sub.set(sk, { key: wi.key, uid: `${wi.key}|${sk}`, label: weekLabelWithYear(wi.key), games: [] });
        sub.get(sk).games.push(f);
    }
    const keys = Array.from(map.keys()).sort();
    if (!keys.length) return [];
    const start = parseLocalDate(keys[0]);
    const end = parseLocalDate(keys[keys.length - 1]);
    const weeks = [];
    const cur = new Date(start);
    while (cur && end && cur <= end) {
        const k = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
        const wi = weekInfo(k);
        const sub = map.get(wi.key);
        if (sub) [...sub.values()].sort((a, b) => (a.games[0].season || "").localeCompare(b.games[0].season || "") || ((stageRank[a.games[0].stage || "regular"] ?? 1) - (stageRank[b.games[0].stage || "regular"] ?? 1))).forEach((w) => weeks.push(w));
        else weeks.push({ key: wi.key, uid: wi.key, label: weekLabelWithYear(wi.key), games: [] });
        cur.setDate(cur.getDate() + 7);
    }
    // Tag weeks that have games (chronologically-first game wins).
    for (const w of weeks) {
        if (w.games.length) {
            const g0 = [...w.games].sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")))[0];
            w.season = g0.season;
            w.stage = g0.stage || "regular";
            w._rank = stageRank[w.stage] ?? 1;
        }
    }
    // Forward-fill empty weeks from the previous tagged week.
    let last = null;
    for (const w of weeks) { if (w.season) last = { season: w.season, stage: w.stage }; else if (last) { w.season = last.season; w.stage = last.stage; } }
    // Back-fill any leading empty weeks from the next tagged week.
    let next = null;
    for (let i = weeks.length - 1; i >= 0; i--) { const w = weeks[i]; if (w.season) next = { season: w.season, stage: w.stage }; else if (next) { w.season = next.season; w.stage = next.stage; } }
    return weeks;
}

export const STAGE_BADGE = { preseason: ["PRE", "#94A3B8"], regular: ["RS", "#9D4CDD"], playoffs: ["PO", "#FBBF24"] };

// ============================ NEW SCHEDULE STYLE ============================
// Large, fully-readable single-horizontal-row-per-game design using the site's
// translucent card language. Admin-tunable dimensions via settings.schedule_layout.

export function ScheduleGameRow({ f, layout, isNext, teamId = null, avail = null }) {
    const { pwhl } = useTeamLookup();
    const { isAdmin } = useAuth();
    const navigate = useNavigate();
    const lay = layout || {};
    const rowH = Number(lay.rowHeight || 64);
    const fs = Number(lay.fontSize || 15);
    const ls = Number(lay.logoSize || 32);
    const showVenue = lay.showVenue !== false;
    const isFinal = f.status === "Final";
    const away = pwhl(f.away_team_id);
    const home = pwhl(f.home_team_id);
    const awayLogo = away.logo_url ? assetUrl(away.logo_url) : null;
    const homeLogo = home.logo_url ? assetUrl(home.logo_url) : null;
    const awayWon = isFinal && f.away_score > f.home_score;
    const homeWon = isFinal && f.home_score > f.away_score;
    const d = parseLocalDate(f.date);

    // Team schedule: which side is the viewed team. League schedule: no single
    // side — we render a Roster/Lineup pair for EACH team flanking the matchup.
    const side = teamId ? (f.home_team_id === teamId ? "home" : f.away_team_id === teamId ? "away" : null) : null;
    const jump = (e, view, whichSide) => { e.preventDefault(); e.stopPropagation(); navigate(`/game/${f.id}?view=${whichSide}_${view}`); };
    const HistBtn = ({ kind, enabled, icon: Icon, whichSide, tabbr, col }) => (
        <button
            type="button"
            disabled={!enabled}
            onClick={(e) => enabled && jump(e, kind, whichSide)}
            data-testid={`fixture-${kind}-btn-${whichSide}-${f.id}`}
            title={enabled ? `View ${tabbr} ${kind}` : `No ${kind} recorded for this game yet`}
            style={enabled ? { borderColor: `${col}80`, background: `${col}1F`, color: col } : undefined}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[9px] font-black uppercase tracking-wider transition-colors ${
                enabled ? "hover:brightness-125" : "border-white/[0.06] text-white/20 cursor-not-allowed"
            }`}
        >
            <Icon className="h-3 w-3" /> {kind === "roster" ? "Roster" : "Lineup"}
        </button>
    );
    const SideHistBtns = ({ whichSide }) => {
        const tid = whichSide === "home" ? f.home_team_id : f.away_team_id;
        const col = TEAM_INFO[tid]?.color || "#9D4CDD";
        const tabbr = (whichSide === "home" ? home : away).abbr;
        return (
            <div className="shrink-0 hidden sm:flex flex-col gap-1" data-testid={`fixture-history-${whichSide}-${f.id}`} onClick={(e) => e.preventDefault()}>
                <HistBtn kind="roster" enabled={!!(avail && avail[`has_${whichSide}_roster`])} icon={ClipboardList} whichSide={whichSide} tabbr={tabbr} col={col} />
                <HistBtn kind="lineup" enabled={!!(avail && avail[`has_${whichSide}_lineup`])} icon={Users} whichSide={whichSide} tabbr={tabbr} col={col} />
            </div>
        );
    };

    return (
        <Link to={`/game/${f.id}`} data-testid={`fixture-${f.id}`}
            style={{ minHeight: rowH }}
            className={`group relative flex items-center gap-3 sm:gap-5 px-3 sm:px-5 rounded-xl border transition-all overflow-hidden
                ${isNext ? "border-white/15 bg-black" : "border-white/10 bg-white/[0.04]"} hover:bg-white/[0.07] hover:border-white/20`}>
            {isAdmin && avail && avail.unlinked_count > 0 && (
                <span
                    data-testid={`fixture-unlinked-alert-${f.id}`}
                    title={`${avail.unlinked_count} unlinked player${avail.unlinked_count === 1 ? "" : "s"} in this game centre — needs manual matching`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/game/${f.id}?view=away_roster`); }}
                    className="absolute top-1.5 right-1.5 z-20 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#F6C453]/15 border border-[#F6C453]/50 text-[#F6C453] text-[9px] font-black tabular-nums hover:bg-[#F6C453]/25 transition-colors">
                    <AlertTriangle className="h-3 w-3" />{avail.unlinked_count}
                </span>
            )}
            {/* date block */}
            <div className="shrink-0 w-12 sm:w-16 text-center leading-tight">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-bold text-white/45">{d ? SHORT_DAYS[d.getDay()] : "TBD"}</div>
                <div className="font-heading font-black text-white tabular-nums" style={{ fontSize: fs + 4 }}>{d ? d.getDate() : "—"}</div>
                <div className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-white/35">{d ? SHORT_MONTHS[d.getMonth()] : ""}</div>
            </div>
            <span className="w-px self-stretch my-3 bg-white/10 shrink-0" />
            {/* matchup */}
            <div className="flex-1 flex items-center gap-2 sm:gap-4 min-w-0">
                {!teamId && <SideHistBtns whichSide="away" />}
                {/* away */}
                <div className="flex-1 flex items-center justify-end gap-2 sm:gap-3 min-w-0">
                    <span className={`truncate text-right ${awayWon ? "text-white font-black" : "text-white/70 font-semibold"}`} style={{ fontSize: fs }}>
                        <span className="hidden sm:inline">{away.name || (away.abbr || "").toUpperCase()}</span>
                        <span className="sm:hidden">{(away.abbr || "").toUpperCase()}</span>
                    </span>
                    {awayLogo ? <img src={awayLogo} alt="" style={{ height: ls, width: ls }} className="object-contain shrink-0" /> : <span style={{ height: ls, width: ls }} className="rounded bg-white/10 shrink-0" />}
                </div>
                {/* center score block — fixed central divider; numbers mirrored & baseline-aligned
                    so a bold (winner) number never sits higher than the dimmed loser number */}
                <div className="shrink-0 grid grid-cols-[1fr_auto_1fr] items-baseline" style={{ minWidth: isFinal ? 64 : 26 }}>
                    <span className={`justify-self-end tabular-nums font-black leading-none ${awayWon ? "text-white" : "text-white/40"}`} style={{ fontSize: fs + 5 }} data-testid={`fixture-score-${f.id}-${f.away_team_id}`}>{isFinal ? f.away_score : ""}</span>
                    <span className="px-1.5 font-black leading-none text-white/30" style={{ fontSize: isFinal ? fs + 5 : fs - 2 }}>{isFinal ? "–" : "@"}</span>
                    <span className={`justify-self-start tabular-nums font-black leading-none ${homeWon ? "text-white" : "text-white/40"}`} style={{ fontSize: fs + 5 }} data-testid={`fixture-score-${f.id}-${f.home_team_id}`}>{isFinal ? f.home_score : ""}</span>
                </div>
                {/* home */}
                <div className="flex-1 flex items-center gap-2 sm:gap-3 min-w-0">
                    {homeLogo ? <img src={homeLogo} alt="" style={{ height: ls, width: ls }} className="object-contain shrink-0" /> : <span style={{ height: ls, width: ls }} className="rounded bg-white/10 shrink-0" />}
                    <span className={`truncate ${homeWon ? "text-white font-black" : "text-white/70 font-semibold"}`} style={{ fontSize: fs }}>
                        <span className="hidden sm:inline">{home.name || (home.abbr || "").toUpperCase()}</span>
                        <span className="sm:hidden">{(home.abbr || "").toUpperCase()}</span>
                    </span>
                </div>
                {!teamId && <SideHistBtns whichSide="home" />}
            </div>
            <span className="w-px self-stretch my-3 bg-white/10 shrink-0" />
            {/* status / time + venue */}
            <div className="shrink-0 w-24 sm:w-40 text-right leading-tight">
                <div className="flex items-center justify-end gap-1">
                    <GameStatusTag f={f} />
                    {isFinal && (
                        <span className="text-[8px] uppercase font-bold text-white/40" data-testid={`fixture-endtag-${f.id}`}>{f.shootout ? "SO" : f.overtime ? ((f.num_ot > 1) ? `OT${f.num_ot}` : "OT") : "REG"}</span>
                    )}
                </div>
                {isNext && <span className="inline-block mt-0.5 text-[8px] uppercase tracking-wider font-bold text-white bg-white/15 rounded px-1.5 py-0.5">Next up</span>}
                {showVenue && f.venue && <div className="text-[9px] sm:text-[10px] text-white/35 leading-tight break-words mt-0.5">{f.venue}</div>}
            </div>
            {side && (
                <div className="shrink-0 flex flex-col gap-1" data-testid={`fixture-history-${f.id}`} onClick={(e) => e.preventDefault()}>
                    <HistBtn kind="roster" enabled={!!(avail && avail[`has_${side}_roster`])} icon={ClipboardList} whichSide={side} tabbr={(side === "home" ? home : away).abbr} col={TEAM_INFO[side === "home" ? f.home_team_id : f.away_team_id]?.color || "#9D4CDD"} />
                    <HistBtn kind="lineup" enabled={!!(avail && avail[`has_${side}_lineup`])} icon={Users} whichSide={side} tabbr={(side === "home" ? home : away).abbr} col={TEAM_INFO[side === "home" ? f.home_team_id : f.away_team_id]?.color || "#9D4CDD"} />
                </div>
            )}
        </Link>
    );
}

function NavBtn({ onClick, disabled, title, testId, children }) {
    return (
        <button onClick={onClick} disabled={disabled} data-testid={testId} title={title}
            className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-white/10 text-white/70 hover:text-white hover:border-white/25 disabled:opacity-25 disabled:cursor-not-allowed transition-colors">
            {children}
        </button>
    );
}

// Single continuous week navigator: « jump-to-prev-games-week | ‹ prev week |
// label + season/stage badge | next week › | jump-to-next-games-week » | Today.
function ScheduleWeekNav({ cur, onPrev, onNext, onPrevGames, onNextGames, onToday, canPrev, canNext, canPrevGames, canNextGames }) {
    const [badge, badgeCol] = STAGE_BADGE[cur.stage] || STAGE_BADGE.regular;
    return (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-2 sm:px-3 py-2.5" data-testid="schedule-period-nav">
            <div className="flex items-center gap-1.5">
                <NavBtn testId="schedule-jump-prev-games" onClick={onPrevGames} disabled={!canPrevGames} title="Jump to previous week with games"><ChevronsLeft className="h-4 w-4" /></NavBtn>
                <NavBtn testId="schedule-period-prev" onClick={onPrev} disabled={!canPrev} title="Previous week"><ChevronLeft className="h-4 w-4" /></NavBtn>
            </div>
            <div className="text-center min-w-0">
                <div className="font-heading text-base sm:text-lg font-black uppercase tracking-tight text-white truncate" data-testid="schedule-period-label">{cur.label}</div>
                <div className="flex items-center justify-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-white/45" data-testid="schedule-period-season">{shortSeason(cur.season)}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider" style={{ color: badgeCol, background: `${badgeCol}22`, border: `1px solid ${badgeCol}55` }} data-testid="schedule-period-stage">{badge}</span>
                    <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold">· {cur.games.length} {cur.games.length === 1 ? "game" : "games"}</span>
                </div>
            </div>
            <div className="flex items-center gap-1.5">
                <NavBtn testId="schedule-period-next" onClick={onNext} disabled={!canNext} title="Next week"><ChevronRight className="h-4 w-4" /></NavBtn>
                <NavBtn testId="schedule-jump-next-games" onClick={onNextGames} disabled={!canNextGames} title="Jump to next week with games"><ChevronsRight className="h-4 w-4" /></NavBtn>
                <button onClick={onToday} data-testid="schedule-jump-today" title="Jump to the present week"
                    className="ml-0.5 inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-white/10 text-white/70 hover:text-white hover:border-white/25 transition-colors text-[10px] uppercase tracking-wider font-bold">
                    <CalendarClock className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Today</span>
                </button>
            </div>
        </div>
    );
}

const money = bizMoney;

// Per-game editable revenue — a small green section attached to each game row
// (Business Mode only). Editing sets an explicit per-game override.
function TeamRevenueSummary({ teamId, fixtures = [], biz, season }) {
    const { pwhl } = useTeamLookup();
    const homeGames = useMemo(() => fixtures.filter((f) => f.home_team_id === teamId), [fixtures, teamId]);
    const seasonHome = useMemo(() => homeGames.filter((f) => f.season === season), [homeGames, season]);
    const sum = (arr) => arr.reduce((s, f) => s + gameRevenue(f, biz), 0);
    const seasonRev = sum(seasonHome);
    const allRev = sum(homeGames);
    const withGate = seasonHome.filter((f) => gameRevenue(f, biz) > 0).length;
    const team = pwhl(teamId);
    const venue = seasonHome.find((f) => f.venue)?.venue || homeGames.find((f) => f.venue)?.venue || "home venue";
    return (
        <div className="rounded-2xl border border-[#22C55E]/30 bg-[#22C55E]/[0.06] p-4 flex flex-wrap items-center justify-between gap-4" data-testid="team-revenue-summary">
            <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#86EFAC]">Home revenue · {team.abbr || teamId}</p>
                <p className="font-mono-stat text-2xl font-black text-white mt-0.5" data-testid="team-revenue-season">{bizMoney(seasonRev)}</p>
                <p className="text-[11px] text-white/50 mt-0.5">{venue} · {withGate}/{seasonHome.length} home games with gate</p>
            </div>
            <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider font-bold text-white/40">All seasons (home)</p>
                <p className="font-mono-stat text-lg font-bold text-[#C9F7D4]" data-testid="team-revenue-alltime">{bizMoney(allRev)}</p>
            </div>
        </div>
    );
}

function GameRevenueChip({ f }) {
    const { biz, setGameRevenue } = useScheduleBusiness();
    const value = gameRevenue(f, biz);
    const overridden = hasGameOverride(f, biz);
    const [draft, setDraft] = useState("");
    const [editing, setEditing] = useState(false);
    const commit = () => { setEditing(false); if (draft !== "" && Number(draft) !== value) setGameRevenue(f.id, draft); };
    return (
        <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-[#22C55E]/25 bg-[#22C55E]/[0.06] px-2.5 py-1.5" data-testid={`schedule-revenue-${f.id}`}>
            <DollarSign className="h-3.5 w-3.5 text-[#22C55E] shrink-0" />
            <span className="text-[9px] uppercase tracking-wider font-bold text-white/45">{overridden ? "Set" : "Est."} gate</span>
            {editing ? (
                <input autoFocus type="number" min="0" defaultValue={value}
                    onChange={(e) => setDraft(e.target.value)} onBlur={commit}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    className="ml-auto w-28 bg-[#0A0B10] border border-[#22C55E]/40 rounded px-2 py-0.5 text-right text-sm font-black tabular-nums text-[#22C55E] outline-none"
                    data-testid={`schedule-revenue-input-${f.id}`} />
            ) : (
                <button type="button" onClick={() => { setDraft(""); setEditing(true); }}
                    className="ml-auto text-sm font-black tabular-nums text-[#22C55E] hover:underline" data-testid={`schedule-revenue-value-${f.id}`}>
                    {money(value)}
                </button>
            )}
            {overridden && !editing && (
                <button type="button" onClick={() => setGameRevenue(f.id, "")} title="Reset to estimate"
                    className="text-[9px] uppercase tracking-wider font-bold text-white/35 hover:text-white" data-testid={`schedule-revenue-reset-${f.id}`}>reset</button>
            )}
        </div>
    );
}

// Bottom-of-week footer: total week revenue + a "set every game this stage" control.
function BusinessWeekFooter({ games, season, stage }) {
    const { biz, setStageDefault } = useScheduleBusiness();
    const total = games.reduce((s, f) => s + gameRevenue(f, biz), 0);
    const stageKey = `${season}|${stage || "regular"}`;
    const curDefault = biz?.stage_defaults?.[stageKey] ?? "";
    const [stageVal, setStageVal] = useState("");
    const stageLabel = { preseason: "Preseason", regular: "Regular Season", playoffs: "Playoffs" }[stage] || stage;
    return (
        <div className="mt-4 rounded-2xl border border-[#22C55E]/25 bg-[#0B0C12] p-3 sm:p-4" data-testid="schedule-business-revenue">
            <div className="flex items-center gap-2 flex-wrap">
                <DollarSign className="h-4 w-4 text-[#22C55E]" />
                <span className="font-heading text-sm font-black uppercase tracking-wider text-white">Total Week Revenue</span>
                <span className="ml-auto text-lg font-black tabular-nums text-[#22C55E]" data-testid="schedule-business-total">{money(total)}</span>
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-bold text-white/45">Ticket price for {shortSeason(season)} {stageLabel} games ($):</span>
                <input type="number" min="0" placeholder={curDefault !== "" ? String(curDefault) : "price"} value={stageVal}
                    onChange={(e) => setStageVal(e.target.value)}
                    className="w-32 bg-[#0A0B10] border border-white/15 rounded px-2 py-1 text-right text-sm font-mono-stat text-white outline-none focus:border-[#22C55E]"
                    data-testid="schedule-stage-default-input" />
                <button type="button" onClick={() => { if (stageVal !== "") { setStageDefault(season, stage || "regular", stageVal); setStageVal(""); } }}
                    className="px-3 py-1 rounded-lg bg-[#22C55E] text-[#0A0B10] text-[11px] font-black uppercase tracking-wider" data-testid="schedule-stage-default-apply">Set price</button>
                {curDefault !== "" && (
                    <button type="button" onClick={() => setStageDefault(season, stage || "regular", "")}
                        className="text-[10px] uppercase tracking-wider font-bold text-white/35 hover:text-white" data-testid="schedule-stage-default-clear">clear</button>
                )}
            </div>
            <p className="text-[10px] text-white/30 italic mt-2">Revenue = attendance × ticket price. Enter a number on a specific game to override it entirely. Games without an attendance aren't counted.</p>
        </div>
    );
}

function StoriesThisWeek({ stories }) {
    if (!stories || !stories.length) return null;
    const list = [...stories].sort((a, b) => b.excitement - a.excitement).slice(0, 6);
    return (
        <div className="mt-4 rounded-2xl border border-[#F5C518]/15 bg-[#0B0C12] p-3 sm:p-4" data-testid="schedule-week-stories">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4" style={{ color: "#F5C518" }} />
                <span className="font-heading text-sm font-black uppercase tracking-wider text-white">Stories This Week</span>
                <Link to="/stories" className="ml-auto text-[10px] uppercase tracking-wider font-black text-[#F5C518] hover:underline" data-testid="schedule-week-stories-link">All stories →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {list.map((s, i) => <MiniStory key={s.id} s={s} testId={`schedule-week-story-${i}`} />)}
            </div>
        </div>
    );
}

// Big month title + prev/next month + Today. Lives ON the page (not the nav bar).
function MonthNav({ month, idx, total, onPrev, onNext, onToday }) {
    const [badge, badgeCol] = STAGE_BADGE[month.stage] || STAGE_BADGE.regular;
    return (
        <div className="flex items-center justify-between gap-3" data-testid="schedule-month-nav">
            <NavBtn testId="schedule-month-prev" onClick={onPrev} disabled={idx <= 0} title="Previous month"><ChevronLeft className="h-5 w-5" /></NavBtn>
            <div className="flex-1 flex flex-col items-center justify-center">
                <div className="flex items-center justify-center gap-3">
                    <h2 className="font-heading font-black uppercase tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl text-center leading-none" data-testid="schedule-month-title">{month.label}</h2>
                    <button onClick={onToday} data-testid="schedule-month-today" title="Jump to the present month"
                        className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-white/60 hover:text-white border border-white/10 hover:border-white/25 rounded-lg px-2 py-1 transition-colors">
                        <CalendarClock className="h-3.5 w-3.5" /> Today
                    </button>
                </div>
                <div className="mt-1 inline-flex items-center gap-1.5" data-testid="schedule-month-stage">
                    <span className="text-[9px] uppercase tracking-wider font-bold text-white/40">{shortSeason(month.season)}</span>
                    <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider" style={{ color: badgeCol, background: `${badgeCol}22`, border: `1px solid ${badgeCol}55` }}>{badge}</span>
                </div>
            </div>
            <NavBtn testId="schedule-month-next" onClick={onNext} disabled={idx >= total - 1} title="Next month"><ChevronRight className="h-5 w-5" /></NavBtn>
        </div>
    );
}

// The month page: games grouped by day (regular-size day heading) with a grid of
// compact matchup widgets — as many per row as fit. No per-widget date (the day
// heading carries it) and no week nav.
function MonthBoard({ month, layout, nextGameId, businessMode = false, teamId = null, availMap = {} }) {
    const byDay = useMemo(() => {
        const m = new Map();
        [...month.games]
            .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")))
            .forEach((f) => { if (!m.has(f.date)) m.set(f.date, []); m.get(f.date).push(f); });
        return [...m.entries()];
    }, [month]);
    if (!month.games.length) {
        return (
            <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center" data-testid="schedule-month-empty">
                <CalendarDays className="mx-auto mb-3 text-white/30" size={24} />
                <p className="text-sm text-white/55">No games this month.</p>
            </div>
        );
    }
    return (
        <div data-testid={`schedule-month-${month.key}`} className="space-y-5">
            {byDay.map(([date, games]) => (
                <div key={date} data-testid={`schedule-day-${date}`}>
                    <div className="flex items-center gap-3 mb-2">
                        <span className="text-[11px] sm:text-xs font-black uppercase tracking-widest text-white/60 whitespace-nowrap">{dayLabel(date)}</span>
                        <span className="h-px flex-1 bg-white/10" />
                        <span className="text-[10px] font-bold text-white/30 tabular-nums">{games.length} game{games.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                        {games.map((f) => (
                            <div key={f.id}>
                                <GameCard f={f} bold={f.id === nextGameId} />
                                {businessMode && <GameRevenueChip f={f} />}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            {businessMode && <BusinessWeekFooter games={month.games} season={month.season} stage={month.stage} />}
        </div>
    );
}


function WeekBoard({ week, layout, nextGameId, weekStories = [], businessMode = false, teamId = null, availMap = {} }) {
    const gap = Number(layout.gap || 10);
    if (!week.games.length) {
        return (
            <div data-testid="schedule-week-empty-wrap">
                <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center" data-testid="schedule-week-empty">
                    <CalendarDays className="mx-auto mb-3 text-white/30" size={24} />
                    <p className="text-sm text-white/55">No games this week.</p>
                    <p className="text-xs text-white/35 mt-1">Use the « / » buttons to jump to the next week with games.</p>
                </div>
                <StoriesThisWeek stories={weekStories} />
            </div>
        );
    }
    const games = [...week.games].sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")));
    return (
        <div>
            <div style={{ display: "flex", flexDirection: "column", gap }} data-testid={`schedule-week-${week.key}`}>
                {games.map((f) => (
                    <div key={f.id}>
                        <ScheduleGameRow f={f} layout={layout} isNext={f.id === nextGameId} teamId={teamId} avail={availMap[f.id]} />
                        {businessMode && <GameRevenueChip f={f} />}
                    </div>
                ))}
            </div>
            {businessMode && <BusinessWeekFooter games={games} season={week.season} stage={week.stage} />}
            <StoriesThisWeek stories={weekStories} />
        </div>
    );
}


// Status / kickoff text shown on each card. Final → FINAL, Live → LIVE,
// upcoming → the scheduled start time (else TBD).
function GameStatusTag({ f }) {
    useTimePrefs();
    if (f.status === "Final") {
        return <span className="text-[10px] font-black uppercase tracking-wider text-[#FBBF24]" data-testid={`fixture-status-${f.id}`}>Final</span>;
    }
    if (f.status === "Live") {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-[#22C55E]" data-testid={`fixture-status-${f.id}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" /> Live
            </span>
        );
    }
    return <span className="text-[10px] font-mono-stat font-bold text-white/80" data-testid={`fixture-status-${f.id}`}>{formatFixtureTime(f.date, f.time)}</span>;
}

// One ultra-compact, fully-clickable game row — both teams on a single line
// so the schedule packs as many games on screen as possible.
function GameCard({ f, sz, bold }) {
    const { pwhl } = useTeamLookup();
    const SZ = sz || { h: "h-9", logo: "h-4 w-4", abbr: "text-[11px]", score: "text-[12px]", status: "text-[10px]", statusW: "w-11" };
    const aw = bold ? "font-black" : "font-semibold";
    const isFinal = f.status === "Final";
    const away = pwhl(f.away_team_id);
    const home = pwhl(f.home_team_id);
    const awayLogo = away.logo_url ? assetUrl(away.logo_url) : null;
    const homeLogo = home.logo_url ? assetUrl(home.logo_url) : null;
    const awayWon = isFinal && f.away_score > f.home_score;
    const homeWon = isFinal && f.home_score > f.away_score;
    // TBD (not final, not live, no scheduled time) → collapse the status column
    // entirely so the matchup packs left with no dead space.
    const isTbd = !isFinal && f.status !== "Live" && !f.time;
    return (
        <Link
            to={`/game/${f.id}`}
            data-testid={`fixture-${f.id}`}
            className={`group flex items-center justify-center sm:justify-start sm:w-fit gap-2 ${SZ.h} px-2.5 rounded-lg border border-white/[0.07] bg-[#111219] hover:border-white/15 hover:bg-[#191b27] transition-colors`}
        >
            {!isTbd && (
                <span className={`${SZ.statusW} shrink-0 flex items-center gap-1 leading-none`}>
                    <GameStatusTag f={f} />
                    {isFinal && (
                        <span className="text-[8px] uppercase font-bold text-white/40" data-testid={`fixture-endtag-${f.id}`}>{f.shootout ? "SO" : f.overtime ? ((f.num_ot > 1) ? `OT${f.num_ot}` : "OT") : "REG"}</span>
                    )}
                </span>
            )}
            {/* Compact matchup — fixed-width columns packed next to the status so
                there's no dead gap; both teams' logo+abbr mirror the centre line
                (scores collapse when there's no result). */}
            <div className="flex items-center gap-1.5">
                <span className={`w-8 text-right ${SZ.abbr} ${aw} truncate ${awayWon ? "text-white" : "text-white/55"}`}>{(away.abbr || "").toUpperCase()}</span>
                {awayLogo ? <img src={awayLogo} alt="" className={`${SZ.logo} object-contain shrink-0`} /> : <span className={`${SZ.logo} rounded bg-white/10 shrink-0`} />}
                {isFinal && <span className={`w-3.5 text-right ${SZ.score} font-black tabular-nums ${awayWon ? "text-white" : "text-white/40"}`} data-testid={`fixture-score-${f.id}-${f.away_team_id}`}>{f.away_score}</span>}
                <span className="w-px h-4 bg-white/10 shrink-0" />
                {isFinal && <span className={`w-3.5 text-left ${SZ.score} font-black tabular-nums ${homeWon ? "text-white" : "text-white/40"}`} data-testid={`fixture-score-${f.id}-${f.home_team_id}`}>{f.home_score}</span>}
                {homeLogo ? <img src={homeLogo} alt="" className={`${SZ.logo} object-contain shrink-0`} /> : <span className={`${SZ.logo} rounded bg-white/10 shrink-0`} />}
                <span className={`w-8 text-left ${SZ.abbr} ${aw} truncate ${homeWon ? "text-white" : "text-white/55"}`}>{(home.abbr || "").toUpperCase()}</span>
            </div>
        </Link>
    );
}

function ScheduleList({ fixtures, summarySet, settings }) {
    const device = useDeviceKind();
    const { bold: schedBold, s: schedSZ } = resolveSize(settings?.schedule_size, device, "schedule");
    const list = useMemo(() => fixtures || [], [fixtures]);
    const nextRef = useRef(null);
    // Per-game predictor (access-zone "Predict" toggle). Shows a score/result
    // control under each card; picks feed the SAME predicted standings grid.
    const { getPref } = usePagePrefs();
    const { user } = useAuth();
    const predictOn = getPref("page_toggle", "sched_predict") === "on";
    const predSeason = (list[0] && list[0].season) || "";
    const { data: predGrid } = useQuery({
        queryKey: ["predictions-grid", predSeason, "regular"],
        queryFn: () => getPredictionsGrid(predSeason, "regular"),
        enabled: predictOn && !!user && !!predSeason,
    });
    const savedPicks = usePwhlPicks(predSeason, predictOn && !!user);
    const picks = useMemo(() => {
        const m = {};
        (predGrid?.teams || []).forEach((t) => (t.games || []).forEach((g) => {
            if (m[g.fixture_id]) return;
            const code = g.predicted_code || "";
            if (!code) return;
            const win = ["W", "OTW", "SOW"].includes(code);
            const result = code.includes("OT") ? "OT" : code.includes("SO") ? "SO" : "REG";
            m[g.fixture_id] = {
                mode: g.predicted_score ? "score" : "quick",
                winner: win ? t.team_id : g.opponent_id,
                home_score: g.predicted_home_score, away_score: g.predicted_away_score, result,
            };
        }));
        return { ...m, ...savedPicks };
    }, [predGrid, savedPicks]);

    // Group by Monday-week, then by day inside each week.
    const weeks = useMemo(() => {
        const byWeek = {};
        list.forEach((f) => {
            const wi = weekInfo(f.date);
            if (!byWeek[wi.key]) byWeek[wi.key] = { key: wi.key, label: wi.label, days: {} };
            const dk = f.date || "tbd";
            (byWeek[wi.key].days[dk] ||= []).push(f);
        });
        return Object.values(byWeek).sort((a, b) => a.key.localeCompare(b.key));
    }, [list]);

    // "Next up" = first day on/after today.
    const todayStr = new Date().toISOString().slice(0, 10);
    const nextDate = useMemo(() => {
        const allDays = list.map((f) => f.date).filter((d) => d && d >= todayStr).sort();
        return allDays[0] || null;
    }, [list, todayStr]);
    // The single next scheduled (not-yet-final) game — gets a featured card.
    const nextGame = useMemo(() => {
        return list
            .filter((f) => f.date && f.date >= todayStr && f.status !== "Final")
            .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")))[0] || null;
    }, [list, todayStr]);
    useEffect(() => {
        if (nextRef.current) nextRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [nextDate]);

    if (list.length === 0) {
        return (
            <div className="rounded-2xl bg-[#0E0F16] border border-white/10 shadow-xl shadow-black/40 p-10 text-center" data-testid="pwhl-schedule-empty">
                <p className="text-sm text-white/70">No fixtures scheduled for this season yet.</p>
                <p className="text-xs text-white/40 mt-2">
                    Add fixtures via <Link to="/admin" className="text-[#9D4CDD] hover:underline">Admin → PWHL Schedule</Link>.
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-5" data-testid="pwhl-schedule-list">
            {nextGame && <NextGameFeature f={nextGame} />}
            {weeks.map((w) => (
                <div key={w.key} data-testid={`schedule-week-${w.key}`}>
                    {/* week header */}
                    <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading text-base font-black uppercase tracking-tight text-white">{w.label}</h3>
                        <span className="h-px flex-1 bg-white/10" />
                    </div>
                    <div className="space-y-2.5">
                        {Object.keys(w.days).sort().map((d) => (
                            <div key={d} data-testid={`schedule-day-${d}`} ref={d === nextDate ? nextRef : null} className={d === nextDate ? "scroll-mt-24" : ""}>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-white/40">{d === "tbd" ? "Date TBD" : dayLabel(d)}</span>
                                    {d === nextDate && (
                                        <span className="text-[9px] uppercase tracking-wider font-bold text-white bg-[#9D4CDD] rounded px-1.5 py-0.5" data-testid="schedule-next-up">Next up</span>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1.5">
                                    {w.days[d].map((f) => (
                                        (predictOn && user) ? (
                                            <div key={f.id} className="flex flex-col">
                                                <GameCard f={f} sz={schedSZ} bold={schedBold} />
                                                <GamePredict key={`gp-${f.id}-${picks[f.id] ? "p" : "e"}`} f={f} initial={picks[f.id]} />
                                            </div>
                                        ) : <GameCard key={f.id} f={f} sz={schedSZ} bold={schedBold} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

// Featured card for the next scheduled game — bigger matchup + projected goalies
// (the saved starting goalie from each team's game lineup) so it never feels empty.
function NextGameFeature({ f }) {
    useTimePrefs();
    const { data: goalies } = useQuery({
        queryKey: ["projected-goalies", f.id],
        queryFn: () => api.get(`/games/${f.id}/projected-goalies`).then((r) => r.data),
    });
    const awayCol = TEAM_INFO[f.away_team_id]?.color || "#9D4CDD";
    const homeCol = TEAM_INFO[f.home_team_id]?.color || "#9D4CDD";
    return (
        <Link
            to={`/game/${f.id}`}
            data-testid="schedule-next-feature"
            className="group relative block rounded-2xl border border-[#9D4CDD]/40 bg-[#111219] overflow-hidden hover:border-[#9D4CDD]/70 transition-colors"
        >
            <div className="absolute inset-0 opacity-20 pointer-events-none"
                style={{ background: `linear-gradient(100deg, ${awayCol} -20%, transparent 42%, transparent 58%, ${homeCol} 120%)` }} />
            <div className="relative p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-white bg-[#9D4CDD] rounded px-2 py-0.5">Next Game</span>
                    <span className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-bold">{dayLabel(f.date)}</span>
                    <span className="ml-auto font-mono-stat text-sm font-bold text-white">{formatFixtureTime(f.date, f.time)}</span>
                </div>
                <div className="flex items-center justify-center gap-4 sm:gap-8">
                    <div className="flex-1 flex flex-col items-center gap-1.5 text-center min-w-0">
                        <TeamBadge teamId={f.away_team_id} size="lg" asLink={false} />
                        <span className="text-xs sm:text-sm font-bold text-white truncate">{TEAM_INFO[f.away_team_id]?.name || f.away_team_id}</span>
                    </div>
                    <span className="font-mono-stat text-xl text-white/40 font-black shrink-0">@</span>
                    <div className="flex-1 flex flex-col items-center gap-1.5 text-center min-w-0">
                        <TeamBadge teamId={f.home_team_id} size="lg" asLink={false} />
                        <span className="text-xs sm:text-sm font-bold text-white truncate">{TEAM_INFO[f.home_team_id]?.name || f.home_team_id}</span>
                    </div>
                </div>
                {/* projected goalies */}
                <div className="mt-4 pt-3 border-t border-white/[0.07]" data-testid="next-game-projected-goalies">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#64748B] font-bold text-center mb-2">Projected Goalies</div>
                    <div className="grid grid-cols-2 gap-3">
                        <ProjectedGoalie g={goalies?.away} />
                        <ProjectedGoalie g={goalies?.home} align="right" />
                    </div>
                </div>
                {f.venue && <p className="text-[10px] text-[#64748B] text-center mt-3 truncate">{f.venue}</p>}
            </div>
        </Link>
    );
}

function ProjectedGoalie({ g, align }) {
    if (!g) {
        return (
            <div className={`flex items-center gap-2 text-[#64748B] ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
                <div className="w-9 h-9 rounded-full bg-[#1E2233] flex items-center justify-center text-xs">?</div>
                <span className="text-xs italic">TBD</span>
            </div>
        );
    }
    return (
        <div className={`flex items-center gap-2 min-w-0 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
            <PlayerAvatar photo={g.photo_url} name={g.name} playerId={g.id} size="sm" />
            <div className="min-w-0">
                <span className="block text-sm font-bold text-white truncate">{g.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-[#9D4CDD] font-bold">Starter</span>
            </div>
        </div>
    );
}
